import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addConductor,
  addLayoutObject,
  braidConductors,
  BRIGHT_WIRE_COLORS,
  CAPACITOR_LIBRARY,
  createConductor,
  createLayoutGroup,
  createLayoutObject,
  DEFAULT_CAN_CAPACITOR_SECTIONS,
  DIODE_LIBRARY,
  duplicateLayoutObject,
  expandLayoutCanvasToFit,
  findClosestCurveParameter,
  formatMeasurement,
  getBraidedConductorPoints,
  getBoardAxialElectrolyticSize,
  getAxialLeadGeometry,
  getConductorCurveControls,
  getLayoutStack,
  getLayoutGroupObjectIds,
  getMullardMustardBodySize,
  getNextLayoutReference,
  getObjectTerminals,
  getPowerTransformerTaps,
  getResistorBandColors,
  getSozoCouplingBodySize,
  getTurretGridGeometry,
  MM_PER_INCH,
  parseMeasurement,
  RESISTOR_LIBRARY,
  removeConductor,
  removeLayoutGroup,
  removeLayoutObject,
  reorderLayoutItem,
  reshapeConductorCurve,
  resolveTerminalPosition,
  updateConductor,
  updateLayoutObject,
  updateCanCapacitorSections,
  updatePowerTransformerTaps,
  unbraidConductors,
} from './layoutDocument';
import { exportLayoutFile } from './layoutExport';
import './LayoutStudio.css';

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const RESISTOR_BODY_SIZES = Object.freeze({
  'carbon-composition': Object.freeze({
    0.5: Object.freeze({ width: 9.5, height: 3.5 }),
    1: Object.freeze({ width: 15, height: 6 }),
    2: Object.freeze({ width: 18, height: 8 }),
  }),
  'iskra-upm': Object.freeze({
    0.5: Object.freeze({ width: 10, height: 4 }),
    1: Object.freeze({ width: 15, height: 5 }),
    2: Object.freeze({ width: 20, height: 8 }),
  }),
  piher: Object.freeze({
    0.5: Object.freeze({ width: 10, height: 3.2 }),
    1: Object.freeze({ width: 13.5, height: 4.3 }),
    2: Object.freeze({ width: 17.5, height: 5.5 }),
  }),
  'metal-film': Object.freeze({
    0.5: Object.freeze({ width: 9, height: 3.2 }),
    1: Object.freeze({ width: 11, height: 4.5 }),
    2: Object.freeze({ width: 15.5, height: 5 }),
  }),
  'metal-oxide': Object.freeze({
    1: Object.freeze({ width: 11.5, height: 4.5 }),
    2: Object.freeze({ width: 15.5, height: 6 }),
    3: Object.freeze({ width: 24.5, height: 9 }),
  }),
  'ceramic-composition': Object.freeze({
    0.5: Object.freeze({ width: 9, height: 3.5 }),
    1: Object.freeze({ width: 16.5, height: 5.5 }),
    2: Object.freeze({ width: 19, height: 7 }),
  }),
});

const getResistorWattageOptions = (construction) => Object.keys(
  RESISTOR_BODY_SIZES[construction] || RESISTOR_BODY_SIZES['carbon-composition']
).map(Number);

const getResistorWattage = (object) => {
  const construction = object.construction || 'carbon-composition';
  const sizes = RESISTOR_BODY_SIZES[construction] || RESISTOR_BODY_SIZES['carbon-composition'];
  return sizes[object.wattage] ? Number(object.wattage) : RESISTOR_LIBRARY[construction]?.wattage || 0.5;
};

const getResistorBodySize = (object) => {
  const wattage = getResistorWattage(object);
  const construction = object.construction || 'carbon-composition';
  return (RESISTOR_BODY_SIZES[construction] || RESISTOR_BODY_SIZES['carbon-composition'])[wattage];
};

const getAxialLeadDefinitions = (object) => {
  const centerY = object.height / 2;
  let bodyWidth;
  if (object.type === 'resistor') {
    bodyWidth = Math.min(getResistorBodySize(object).width, object.width - 4);
  } else if (object.type === 'diode') {
    const definition = DIODE_LIBRARY[object.construction || 'rectifier'] || DIODE_LIBRARY.rectifier;
    bodyWidth = Math.min(definition.bodyWidth, object.width - 4);
  } else if (object.type === 'capacitor' && object.packageStyle !== 'radial' && object.packageStyle !== 'can') {
    bodyWidth = object.construction === 'mullard-mustard'
      ? Math.min(getMullardMustardBodySize(object.value).width, object.width - 4)
      : object.construction === 'sozo-coupling'
        ? Math.min(getSozoCouplingBodySize(object.value).width, object.width - 4)
        : object.width * (CAPACITOR_LIBRARY[object.construction || 'axial-film']?.bodyFraction || 0.5);
  } else {
    return [];
  }
  const bodyX = (object.width - bodyWidth) / 2;
  const terminalIds = object.type === 'diode' ? ['anode', 'cathode'] : ['lead-1', 'lead-2'];
  return terminalIds.map((terminalId, index) => ({
    terminalId,
    ...getAxialLeadGeometry(object, terminalId, {
      x: index === 0 ? bodyX : bodyX + bodyWidth,
      y: centerY,
    }),
  }));
};

const AxialLeads = ({ object }) => (
  <g>
    {getAxialLeadDefinitions(object).map(lead => (
      <path
        key={lead.terminalId}
        d={`M ${lead.bodyAnchor.x} ${lead.bodyAnchor.y} L ${lead.hinge.x} ${lead.hinge.y} L ${lead.terminal.x} ${lead.terminal.y}`}
        className="layout-axial-lead"
        data-testid={`layout-lead-${lead.terminalId}`}
      />
    ))}
  </g>
);

const toObjectLocalPoint = (object, point) => {
  const rotation = -(object.rotation || 0) * Math.PI / 180;
  const center = { x: object.x + object.width / 2, y: object.y + object.height / 2 };
  const relative = { x: point.x - center.x, y: point.y - center.y };
  return {
    x: object.width / 2 + relative.x * Math.cos(rotation) - relative.y * Math.sin(rotation),
    y: object.height / 2 + relative.x * Math.sin(rotation) + relative.y * Math.cos(rotation),
  };
};

const resolveLeadAttachments = (document, object) => {
  if (!object.leads || !Object.values(object.leads).some(lead => lead.connection)) return object;
  const leads = Object.fromEntries(Object.entries(object.leads).map(([terminalId, lead]) => {
    if (!lead.connection) return [terminalId, lead];
    const targetPosition = resolveTerminalPosition(document, lead.connection);
    return [terminalId, targetPosition
      ? { ...lead, terminal: toObjectLocalPoint(object, targetPosition) }
      : lead];
  }));
  return { ...object, leads };
};

const WIRE_GAUGE_WIDTHS = Object.freeze({
  18: 3.8,
  20: 3,
  22: 2.4,
  24: 1.9,
});

const getWireStrokeWidth = (gauge) => WIRE_GAUGE_WIDTHS[gauge] || WIRE_GAUGE_WIDTHS[22];

const ELECTROLYTIC_SLEEVE_COLORS = Object.freeze([
  { name: 'Ivory', value: '#eeeade', highlight: '#fffef8', shadow: '#b9b3a6', text: '#202422' },
  { name: 'Brown', value: '#8a4f2a', highlight: '#c47a48', shadow: '#56301a', text: '#fffdf2' },
  { name: 'Black', value: '#34393b', highlight: '#707779', shadow: '#171a1b', text: '#ffffff' },
  { name: 'Blue', value: '#347fa3', highlight: '#78bed8', shadow: '#20546d', text: '#111719' },
  { name: 'Silver', value: '#b8bfbd', highlight: '#f1f4f2', shadow: '#777f7d', text: '#202829' },
  { name: 'Orange', value: '#d58a12', highlight: '#f5bf32', shadow: '#995d07', text: '#2d260f' },
]);

const TurretBoard = ({ object }) => {
  const { columns, rows, spacingX, spacingY, startX, startY } = getTurretGridGeometry(object);
  const turrets = [];

  for (let row = 1; row <= rows; row += 1) {
    for (let column = 1; column <= columns; column += 1) {
      turrets.push(
        <g key={`${row}-${column}`}>
          <circle cx={startX + (column - 1) * spacingX} cy={startY + (row - 1) * spacingY} r="2.5" className="layout-turret-ring" />
          <circle cx={startX + (column - 1) * spacingX} cy={startY + (row - 1) * spacingY} r="0.8" className="layout-turret-hole" />
        </g>
      );
    }
  }

  return (
    <g>
      <rect width={object.width} height={object.height} rx="2" className="layout-board-body" />
      <rect x="2" y="2" width={object.width - 4} height={object.height - 4} rx="1" className="layout-board-inset" />
      {turrets}
    </g>
  );
};

const BlankTurretBoard = ({ object }) => (
  <g>
    <rect width={object.width} height={object.height} rx="2" className="layout-board-body" />
    <rect x="2" y="2" width={object.width - 4} height={object.height - 4} rx="1" className="layout-board-inset" />
  </g>
);

const StandaloneTurret = ({ object }) => (
  <g>
    <circle cx={object.width / 2} cy={object.height / 2} r={Math.min(object.width, object.height) / 2} className="layout-turret-ring" />
    <circle cx={object.width / 2} cy={object.height / 2} r={Math.min(object.width, object.height) * 0.16} className="layout-turret-hole" />
  </g>
);

const Chassis = ({ object }) => (
  <g>
    <rect width={object.width} height={object.height} rx="5" className="layout-chassis-body" />
    <rect x="3" y="3" width={object.width - 6} height={object.height - 6} rx="3" className="layout-chassis-inset" />
    {[
      [10, 10],
      [object.width - 10, 10],
      [10, object.height - 10],
      [object.width - 10, object.height - 10],
    ].map(([cx, cy], index) => (
      <g key={index}>
        <circle cx={cx} cy={cy} r="3.5" className="layout-mount-ring" />
        <circle cx={cx} cy={cy} r="1.6" className="layout-mount-hole" />
      </g>
    ))}
  </g>
);

const TubeSocket = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const centerX = object.width / 2;
  const centerY = object.height / 2;
  const isOctal = object.pinCount === 8;
  const isMcmurdo = isOctal && object.construction === 'mcmurdo';
  const isPhenolicNoval = !isOctal && object.construction === 'phenolic-noval';
  if (isMcmurdo) {
    const bodyRadius = 15;
    const mountOffset = 19;
    const mountDelta = mountOffset / Math.sqrt(2);
    const mountHoles = [
      { x: centerX + mountDelta, y: centerY - mountDelta },
      { x: centerX - mountDelta, y: centerY + mountDelta },
    ];
    return (
      <g data-construction="mcmurdo" data-overall-length="50" data-mount-hole-centers="38" data-cutout-diameter="30">
        <rect
          x="1"
          y={centerY - 5.5}
          width={object.width - 2}
          height="11"
          rx="5.5"
          transform={`rotate(-45 ${centerX} ${centerY})`}
          className="layout-socket-mcmurdo-bracket"
        />
        <circle cx={centerX} cy={centerY} r="17.5" className="layout-socket-mcmurdo-retaining-ring" />
        {terminals.map(terminal => (
          <circle
            key={`phenolic-lobe-${terminal.id}`}
            cx={centerX + (terminal.x - centerX) * 0.86}
            cy={centerY + (terminal.y - centerY) * 0.86}
            r="4.1"
            className="layout-socket-mcmurdo-phenolic-lobe"
          />
        ))}
        <circle cx={centerX} cy={centerY} r={bodyRadius} data-body-diameter="30" className="layout-socket-mcmurdo-phenolic" />
        {mountHoles.map((hole, index) => (
          <circle key={index} cx={hole.x} cy={hole.y} r="2.6" className="layout-socket-mcmurdo-mount-hole" />
        ))}
        <circle cx={centerX} cy={centerY} r={object.width * 0.17} className="layout-socket-mcmurdo-well" />
        <path d={`M ${centerX - 2.7} ${centerY + 5.2} L ${centerX} ${centerY + 2.4} L ${centerX + 2.7} ${centerY + 5.2}`} className="layout-socket-mcmurdo-keyway" />
        {terminals.map(terminal => {
          const angle = Math.atan2(terminal.y - centerY, terminal.x - centerX) * 180 / Math.PI + 66;
          return (
            <g key={terminal.id} transform={`translate(${terminal.x} ${terminal.y}) rotate(${angle})`} className="layout-socket-mcmurdo-lug" data-sweep-angle="-24">
              <path d="M -2.7 3.7 Q -3 2.8 -2.35 1.7 L -2 -3.4 Q 0 -4.4 2 -3.4 L 2.35 1.7 Q 3 2.8 2.7 3.7 Z" />
              <circle cx="0" cy="0" r="1.45" className="layout-socket-mcmurdo-lug-hole" />
              <circle cx="0" cy="-3" r="0.75" className="layout-socket-mcmurdo-lug-rivet" />
            </g>
          );
        })}
        {terminals.map((terminal, index) => (
          <text
            key={`pin-number-${terminal.id}`}
            x={centerX + (terminal.x - centerX) * 0.67}
            y={centerY + (terminal.y - centerY) * 0.67 + 1}
            textAnchor="middle"
            className="layout-pin-number layout-socket-mcmurdo-pin-number"
          >{index + 1}</text>
        ))}
      </g>
    );
  }
  if (isPhenolicNoval) {
    const bodyRadius = object.width * 0.36;
    const wellRadius = object.width * 0.105;
    return (
      <g data-construction="phenolic-noval">
        <path
          d={`M 1 ${centerY} Q 1 ${centerY - 5.7} 6.5 ${centerY - 7.2} L ${centerX - 8.5} 3.2 Q ${centerX} 0.8 ${centerX + 8.5} 3.2 L ${object.width - 6.5} ${centerY - 7.2} Q ${object.width - 1} ${centerY - 5.7} ${object.width - 1} ${centerY} Q ${object.width - 1} ${centerY + 5.7} ${object.width - 6.5} ${centerY + 7.2} L ${centerX + 8.5} ${object.height - 3.2} Q ${centerX} ${object.height - 0.8} ${centerX - 8.5} ${object.height - 3.2} L 6.5 ${centerY + 7.2} Q 1 ${centerY + 5.7} 1 ${centerY} Z`}
          className="layout-socket-noval-saddle"
        />
        <circle cx="4.2" cy={centerY} r="2.5" className="layout-socket-noval-mount-fastener" />
        <circle cx={object.width - 4.2} cy={centerY} r="2.5" className="layout-socket-noval-mount-fastener" />
        <circle cx={centerX} cy={centerY} r={bodyRadius + 1.1} className="layout-socket-noval-retaining-ring" />
        <circle cx={centerX} cy={centerY} r={bodyRadius} className="layout-socket-noval-phenolic" />
        {terminals.map(terminal => {
          const angle = Math.atan2(terminal.y - centerY, terminal.x - centerX) * 180 / Math.PI + 90;
          return (
            <g key={`rib-${terminal.id}`} transform={`translate(${centerX} ${centerY}) rotate(${angle})`} className="layout-socket-noval-rib">
              <path d={`M -1.45 ${-wellRadius - 1} L -2.2 ${-bodyRadius + 2.2} H 2.2 L 1.45 ${-wellRadius - 1} Z`} />
            </g>
          );
        })}
        <circle cx={centerX} cy={centerY} r={wellRadius + 1.7} className="layout-socket-noval-well-rim" />
        <circle cx={centerX} cy={centerY} r={wellRadius} className="layout-socket-noval-well" />
        {terminals.map((terminal, index) => {
          const angle = Math.atan2(terminal.y - centerY, terminal.x - centerX) * 180 / Math.PI + 90;
          return (
            <g key={terminal.id} transform={`translate(${terminal.x} ${terminal.y}) rotate(${angle})`} className="layout-socket-noval-contact">
              <path d="M -1.55 2.5 Q 0 3.25 1.55 2.5 L 1.2 -2.4 Q 0 -3.1 -1.2 -2.4 Z" />
              <ellipse cx="0" cy="0.5" rx="0.75" ry="1.15" className="layout-socket-noval-contact-opening" />
            </g>
          );
        })}
        {terminals.map((terminal, index) => (
          <text
            key={`noval-pin-number-${terminal.id}`}
            x={centerX + (terminal.x - centerX) * 0.62}
            y={centerY + (terminal.y - centerY) * 0.62 + 0.9}
            textAnchor="middle"
            className="layout-pin-number layout-socket-noval-pin-number"
          >{index + 1}</text>
        ))}
      </g>
    );
  }
  return (
    <g data-construction={isOctal ? 'generic-octal' : 'generic-noval'}>
      <rect x="1" y={centerY - 5} width={object.width - 2} height="10" rx="5" className="layout-socket-bracket" />
      <circle cx="4" cy={centerY} r="2" className="layout-socket-mount-hole" />
      <circle cx={object.width - 4} cy={centerY} r="2" className="layout-socket-mount-hole" />
      <circle cx={centerX} cy={centerY} r={object.width * 0.41} className="layout-socket-flange" />
      <circle cx={centerX} cy={centerY} r={object.width * 0.32} className="layout-socket-well" />
      <circle cx={centerX} cy={centerY} r={object.width * 0.105} className="layout-socket-key" />
      {isOctal && <path d={`M ${centerX - 2.5} ${centerY - 4} L ${centerX} ${centerY - 1} L ${centerX + 2.5} ${centerY - 4}`} className="layout-socket-keyway" />}
      {terminals.map((terminal, index) => (
        <g key={terminal.id}>
          <circle cx={terminal.x} cy={terminal.y} r={isOctal ? 3.3 : 2.8} className="layout-socket-pin-collar" />
          <circle cx={terminal.x} cy={terminal.y} r={isOctal ? 1.65 : 1.35} className="layout-socket-pin" />
          <text
            x={centerX + (terminal.x - centerX) * 0.67}
            y={centerY + (terminal.y - centerY) * 0.67 + 1.2}
            textAnchor="middle"
            className="layout-pin-number"
          >{index + 1}</text>
        </g>
      ))}
    </g>
  );
};

const AxialResistor = ({ object, displayMode }) => {
  const centerY = object.height / 2;
  const construction = object.construction || 'carbon-composition';
  const size = getResistorBodySize(object);
  const bodyWidth = Math.min(size.width, object.width - 4);
  const bodyHeight = Math.min(size.height, object.height - 1);
  const bodyX = (object.width - bodyWidth) / 2;
  const bodyTop = centerY - bodyHeight / 2;
  const bodyBottom = centerY + bodyHeight / 2;
  const isMetalFilm = construction === 'metal-film';
  const bands = getResistorBandColors(object.value, isMetalFilm ? 3 : 2);
  const isCarbonComposition = construction === 'carbon-composition';
  const isIskra = construction === 'iskra-upm';
  const isPiher = construction === 'piher';
  const isCeramicComposition = construction === 'ceramic-composition';
  const isMetalOxide = construction === 'metal-oxide';
  const hasMoldedBody = isCeramicComposition || isMetalFilm || isMetalOxide;
  const hasWrappedBands = isCarbonComposition || isIskra || isPiher || hasMoldedBody;
  const wrappedBandPositions = isIskra
    ? [0.3, 0.53, 0.69, 0.84]
    : isPiher ? [0.2, 0.4, 0.6, 0.82]
      : isCeramicComposition ? [0.16, 0.4, 0.62, 0.84]
        : isMetalFilm ? [0.16, 0.32, 0.48, 0.64, 0.84]
          : isMetalOxide ? [0.18, 0.42, 0.6, 0.82] : [0.18, 0.34, 0.58, 0.82];
  const bodyClipId = `layout-resistor-body-clip-${object.id}`;
  const shoulder = Math.min(bodyHeight * 0.24, bodyWidth * 0.08);
  const neckHalfHeight = bodyHeight * 0.3;
  const moldedBodyPath = `M ${bodyX} ${centerY - neckHalfHeight} Q ${bodyX + shoulder * 0.25} ${bodyTop + shoulder * 0.25} ${bodyX + shoulder} ${bodyTop} H ${bodyX + bodyWidth - shoulder} Q ${bodyX + bodyWidth - shoulder * 0.25} ${bodyTop + shoulder * 0.25} ${bodyX + bodyWidth} ${centerY - neckHalfHeight} V ${centerY + neckHalfHeight} Q ${bodyX + bodyWidth - shoulder * 0.25} ${bodyBottom - shoulder * 0.25} ${bodyX + bodyWidth - shoulder} ${bodyBottom} H ${bodyX + shoulder} Q ${bodyX + shoulder * 0.25} ${bodyBottom - shoulder * 0.25} ${bodyX} ${centerY + neckHalfHeight} Z`;
  return (
    <g data-construction={construction}>
      <AxialLeads object={object} />
      {hasWrappedBands ? (
        <>
          <defs>
            <clipPath id={bodyClipId}>
              {hasMoldedBody
                ? <path d={moldedBodyPath} />
                : <rect x={bodyX} y={bodyTop} width={bodyWidth} height={bodyHeight} rx={bodyHeight * 0.2} />}
            </clipPath>
          </defs>
          {hasMoldedBody ? (
            <path
              d={moldedBodyPath}
              data-body-height={bodyHeight}
              data-body-width={bodyWidth}
              className={`layout-resistor-body layout-resistor-${construction}`}
            />
          ) : (
            <rect
              x={bodyX}
              y={bodyTop}
              width={bodyWidth}
              height={bodyHeight}
              rx={bodyHeight * 0.2}
              data-body-height={bodyHeight}
              data-body-width={bodyWidth}
              className={`layout-resistor-body layout-resistor-${construction}`}
            />
          )}
        </>
      ) : (
        <path
          d={`M ${bodyX} ${centerY - 1.8} L ${bodyX + 2.2} ${bodyTop} H ${bodyX + bodyWidth - 2.2} L ${bodyX + bodyWidth} ${centerY - 1.8} V ${centerY + 1.8} L ${bodyX + bodyWidth - 2.2} ${bodyBottom} H ${bodyX + 2.2} L ${bodyX} ${centerY + 1.8} Z`}
          data-body-height={bodyHeight}
          data-body-width={bodyWidth}
          className={`layout-resistor-body layout-resistor-${construction}`}
        />
      )}
      {displayMode === 'bands' ? (hasWrappedBands
        ? wrappedBandPositions.map((position, index) => (
          <rect
            key={position}
            data-testid={`resistor-band-${index + 1}`}
            x={bodyX + bodyWidth * position - bodyWidth * 0.045}
            y={bodyTop}
            width={bodyWidth * (isMetalFilm ? 0.075 : isMetalOxide ? 0.07 : 0.09)}
            height={bodyHeight}
            fill={bands[index]}
            clipPath={`url(#${bodyClipId})`}
            className="layout-resistor-band-wrap"
          />
        ))
        : [0.34, 0.44, 0.54, 0.64].map((position, index) => (
          <line
            key={position}
            data-testid={`resistor-band-${index + 1}`}
            x1={bodyX + bodyWidth * position}
            y1={bodyTop + 0.45}
            x2={bodyX + bodyWidth * position}
            y2={bodyBottom - 0.45}
            stroke={bands[index]}
            className="layout-resistor-band"
          />
        ))) : (
        <text x={object.width / 2} y={centerY + 1.35} textAnchor="middle" className="layout-component-value layout-resistor-value">{object.value}</text>
      )}
      {hasWrappedBands && (
        hasMoldedBody ? (
          <path
            d={moldedBodyPath}
            clipPath={`url(#${bodyClipId})`}
            className={`layout-resistor-cylinder-shade${isCeramicComposition ? ' is-ceramic' : isMetalFilm ? ' is-metal-film' : ' is-metal-oxide'}`}
          />
        ) : (
          <rect
            x={bodyX}
            y={bodyTop}
            width={bodyWidth}
            height={bodyHeight}
            rx={bodyHeight * 0.2}
            clipPath={`url(#${bodyClipId})`}
            className="layout-resistor-cylinder-shade"
          />
        )
      )}
    </g>
  );
};

const AxialCapacitor = ({ object }) => {
  const centerY = object.height / 2;
  const construction = object.construction || 'axial-film';
  const isMullardMustard = construction === 'mullard-mustard';
  const isSozoCoupling = construction === 'sozo-coupling';
  const isSprague30D = construction === 'sprague-30d-electrolytic';
  const mustardBodySize = getMullardMustardBodySize(object.value);
  const sozoBodySize = getSozoCouplingBodySize(object.value);
  const bodyFraction = CAPACITOR_LIBRARY[construction]?.bodyFraction || 0.5;
  const bodyWidth = isMullardMustard
    ? Math.min(mustardBodySize.width, object.width - 4)
    : isSozoCoupling
      ? Math.min(sozoBodySize.width, object.width - 4)
      : object.width * bodyFraction;
  const bodyX = (object.width - bodyWidth) / 2;
  const bodyHeight = isMullardMustard
    ? Math.min(mustardBodySize.height, object.height - 2)
    : isSozoCoupling
      ? Math.min(sozoBodySize.height, object.height - 2)
      : object.height - 2;
  const bodyTop = centerY - bodyHeight / 2;
  const bodyBottom = bodyTop + bodyHeight;
  const endRadius = construction === 'mullard-mustard'
    ? bodyHeight / 2
    : construction === 'sozo-coupling' ? 0
      : construction === 'sprague-30d-electrolytic' ? 1.2
        : construction === 'board-axial-electrolytic' ? bodyHeight / 2
        : construction === 'axial-electrolytic' ? 2.5 : 2;
  const isBoardElectrolytic = construction === 'board-axial-electrolytic';
  const isElectrolytic = ['axial-electrolytic', 'board-axial-electrolytic', 'sprague-30d-electrolytic'].includes(construction);
  const sleeveColor = ELECTROLYTIC_SLEEVE_COLORS.find(color => color.value === object.sleeveColor)
    || ELECTROLYTIC_SLEEVE_COLORS[2];
  const sleeveGradientId = `layout-electrolytic-sleeve-${object.id}`;
  const mustardGradientId = `layout-mustard-body-${object.id}`;
  const mustardClipId = `layout-mustard-clip-${object.id}`;
  const mustardTextureId = `layout-mustard-texture-${object.id}`;
  const sozoGradientId = `layout-sozo-body-${object.id}`;
  const sozoClipId = `layout-sozo-clip-${object.id}`;
  const mustardShoulder = Math.min(bodyHeight * 0.34, bodyWidth * 0.09);
  const mustardNeckHalfHeight = bodyHeight * 0.24;
  const mustardBodyPath = `M ${bodyX} ${centerY - mustardNeckHalfHeight} Q ${bodyX + mustardShoulder * 0.2} ${bodyTop + mustardShoulder * 0.24} ${bodyX + mustardShoulder} ${bodyTop} H ${bodyX + bodyWidth - mustardShoulder} Q ${bodyX + bodyWidth - mustardShoulder * 0.2} ${bodyTop + mustardShoulder * 0.24} ${bodyX + bodyWidth} ${centerY - mustardNeckHalfHeight} V ${centerY + mustardNeckHalfHeight} Q ${bodyX + bodyWidth - mustardShoulder * 0.2} ${bodyBottom - mustardShoulder * 0.24} ${bodyX + bodyWidth - mustardShoulder} ${bodyBottom} H ${bodyX + mustardShoulder} Q ${bodyX + mustardShoulder * 0.2} ${bodyBottom - mustardShoulder * 0.24} ${bodyX} ${centerY + mustardNeckHalfHeight} Z`;
  const sozoShoulder = Math.min(bodyHeight * 0.18, bodyWidth * 0.045);
  const sozoNeckHalfHeight = bodyHeight * 0.39;
  const sozoBodyPath = `M ${bodyX} ${centerY - sozoNeckHalfHeight} Q ${bodyX + sozoShoulder * 0.35} ${bodyTop} ${bodyX + sozoShoulder} ${bodyTop} H ${bodyX + bodyWidth - sozoShoulder} Q ${bodyX + bodyWidth - sozoShoulder * 0.35} ${bodyTop} ${bodyX + bodyWidth} ${centerY - sozoNeckHalfHeight} V ${centerY + sozoNeckHalfHeight} Q ${bodyX + bodyWidth - sozoShoulder * 0.35} ${bodyBottom} ${bodyX + bodyWidth - sozoShoulder} ${bodyBottom} H ${bodyX + sozoShoulder} Q ${bodyX + sozoShoulder * 0.35} ${bodyBottom} ${bodyX} ${centerY + sozoNeckHalfHeight} Z`;
  const sozoMarkingSize = Math.min(2.05, bodyHeight * 0.27);
  const sozoBrandSize = Math.min(2.7, bodyHeight * 0.32);
  const marking = `${object.value} ${object.voltageRating || 50}V`;
  return (
    <g data-construction={construction}>
      <AxialLeads object={object} />
      {isMullardMustard && (
        <defs>
          <clipPath id={mustardClipId}>
            <path d={mustardBodyPath} />
          </clipPath>
          <linearGradient id={mustardGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#9e741f" />
            <stop offset="0.14" stopColor="#e3bd58" />
            <stop offset="0.33" stopColor="#d2a83e" />
            <stop offset="0.7" stopColor="#c3952f" />
            <stop offset="0.9" stopColor="#ab7d22" />
            <stop offset="1" stopColor="#7f5a18" />
          </linearGradient>
          <filter id={mustardTextureId} x="-10%" y="-20%" width="120%" height="140%">
            <feTurbulence type="fractalNoise" baseFrequency="0.18 0.7" numOctaves="2" seed="7" result="texture" />
            <feComposite in="texture" in2="SourceGraphic" operator="in" result="clippedTexture" />
            <feBlend in="SourceGraphic" in2="clippedTexture" mode="soft-light" />
          </filter>
        </defs>
      )}
      {isSozoCoupling && (
        <defs>
          <clipPath id={sozoClipId}>
            <path d={sozoBodyPath} />
          </clipPath>
          <linearGradient id={sozoGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#b9a83d" />
            <stop offset="0.13" stopColor="#fff39a" />
            <stop offset="0.34" stopColor="#f3e67c" />
            <stop offset="0.7" stopColor="#e2d366" />
            <stop offset="0.9" stopColor="#cab74a" />
            <stop offset="1" stopColor="#9f8d32" />
          </linearGradient>
        </defs>
      )}
      {isElectrolytic && (
        <defs>
          <clipPath id={`layout-electrolytic-clip-${object.id}`}>
            <rect x={bodyX} y="1" width={bodyWidth} height={object.height - 2} rx={endRadius} />
          </clipPath>
          <linearGradient id={sleeveGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={isSprague30D ? '#777b7b' : sleeveColor.shadow} />
            <stop offset="0.18" stopColor={isSprague30D ? '#e4e6e3' : sleeveColor.highlight} />
            <stop offset="0.5" stopColor={isSprague30D ? '#b8bcba' : sleeveColor.value} />
            <stop offset="0.82" stopColor={isSprague30D ? '#858988' : sleeveColor.shadow} />
            <stop offset="1" stopColor={isSprague30D ? '#d7d9d6' : sleeveColor.highlight} />
          </linearGradient>
        </defs>
      )}
      {isMullardMustard ? (
        <>
          <path
            d={mustardBodyPath}
            data-body-width={bodyWidth}
            data-body-height={bodyHeight}
            className="layout-capacitor-body layout-capacitor-mullard-mustard"
            style={{ fill: `url(#${mustardGradientId})` }}
          />
          <path
            d={mustardBodyPath}
            clipPath={`url(#${mustardClipId})`}
            filter={`url(#${mustardTextureId})`}
            className="layout-capacitor-mustard-texture"
          />
          <path
            d={`M ${bodyX + mustardShoulder} ${bodyTop + bodyHeight * 0.16} Q ${bodyX + bodyWidth / 2} ${bodyTop - bodyHeight * 0.02} ${bodyX + bodyWidth - mustardShoulder} ${bodyTop + bodyHeight * 0.16}`}
            clipPath={`url(#${mustardClipId})`}
            className="layout-capacitor-mustard-highlight"
          />
        </>
      ) : isSozoCoupling ? (
        <>
          <path
            d={sozoBodyPath}
            data-body-width={bodyWidth}
            data-body-height={bodyHeight}
            className="layout-capacitor-body layout-capacitor-sozo-coupling"
            style={{ fill: `url(#${sozoGradientId})` }}
          />
          {[bodyX + bodyWidth * 0.055, bodyX + bodyWidth * 0.945].map((seamX, index) => (
            <rect
              key={seamX}
              x={seamX - bodyWidth * 0.009}
              y={bodyTop}
              width={bodyWidth * 0.018}
              height={bodyHeight}
              clipPath={`url(#${sozoClipId})`}
              className="layout-capacitor-sozo-seam"
              data-testid={`sozo-end-seam-${index + 1}`}
            />
          ))}
          <path
            d={`M ${bodyX + sozoShoulder} ${bodyTop + bodyHeight * 0.15} H ${bodyX + bodyWidth - sozoShoulder}`}
            clipPath={`url(#${sozoClipId})`}
            className="layout-capacitor-sozo-highlight"
          />
        </>
      ) : isSprague30D ? (
        <path
          d={`M ${bodyX + 1.45} ${bodyTop + 0.25} H ${bodyX + bodyWidth - 1.35} Q ${bodyX + bodyWidth - 0.75} ${bodyTop + 0.55} ${bodyX + bodyWidth - 0.5} ${bodyTop + 1.4} L ${bodyX + bodyWidth + 0.35} ${centerY - 0.4} V ${centerY + 0.4} L ${bodyX + bodyWidth - 0.5} ${bodyBottom - 1.4} Q ${bodyX + bodyWidth - 0.75} ${bodyBottom - 0.55} ${bodyX + bodyWidth - 1.35} ${bodyBottom - 0.25} H ${bodyX + 1.45} Q ${bodyX + 0.75} ${bodyBottom + 0.15} ${bodyX + 0.4} ${bodyBottom - 0.65} Q ${bodyX - 0.1} ${centerY} ${bodyX + 0.4} ${bodyTop + 0.65} Q ${bodyX + 0.75} ${bodyTop - 0.15} ${bodyX + 1.45} ${bodyTop + 0.25} Z`}
          data-body-width={bodyWidth}
          data-body-height={bodyHeight}
          className="layout-capacitor-body layout-capacitor-sprague-30d-electrolytic"
          style={{ fill: `url(#${sleeveGradientId})` }}
        />
      ) : (
        <rect
          x={bodyX}
          y={bodyTop}
          width={bodyWidth}
          height={bodyHeight}
          rx={endRadius}
          data-body-width={bodyWidth}
          className={`layout-capacitor-body layout-capacitor-${construction}`}
          style={isElectrolytic ? { fill: `url(#${sleeveGradientId})` } : undefined}
        />
      )}
      {isElectrolytic && (
        isSprague30D ? (
          <>
            <path
              d={`M ${bodyX + 1.45} ${bodyTop + 0.35} Q ${bodyX + 0.65} ${centerY} ${bodyX + 1.45} ${bodyBottom - 0.35}`}
              className="layout-capacitor-sprague-rolled-end"
            />
            <path
              d={`M ${bodyX + bodyWidth - 1.4} ${bodyTop + 0.45} Q ${bodyX + bodyWidth - 0.7} ${centerY} ${bodyX + bodyWidth - 1.4} ${bodyBottom - 0.45}`}
              className="layout-capacitor-sprague-seal"
            />
            <path d={`M ${bodyX + 2.1} ${bodyTop + 0.7} H ${bodyX + bodyWidth - 2.1}`} className="layout-capacitor-sprague-highlight" />
            <text x={object.width / 2 - 0.7} y={centerY - 0.2} textAnchor="middle" className="layout-capacitor-sprague-marking">{object.value} {object.voltageRating || 100}VDC</text>
            <text x={object.width / 2 - 0.7} y={centerY + 2.7} textAnchor="middle" className="layout-capacitor-sprague-brand">SPRAGUE 30D</text>
            <text x={bodyX + bodyWidth - 2.6} y={centerY + 0.8} textAnchor="middle" className="layout-capacitor-sprague-positive">+</text>
          </>
        ) : isBoardElectrolytic ? (
          <>
            <ellipse cx={bodyX + 0.8} cy={centerY} rx="1.3" ry={bodyHeight * 0.42} className="layout-capacitor-board-negative-end" />
            <ellipse cx={bodyX + bodyWidth - 0.8} cy={centerY} rx="1.45" ry={bodyHeight * 0.42} className="layout-capacitor-board-positive-end" />
            <text x={object.width / 2 - 1.5} y={centerY - 0.4} textAnchor="middle" className="layout-capacitor-board-marking">{object.value}</text>
            <text x={object.width / 2 - 1.5} y={centerY + 2.6} textAnchor="middle" className="layout-capacitor-board-marking">{object.voltageRating || 10} VDC</text>
            <text x={bodyX + bodyWidth - 4} y={centerY + 1.4} textAnchor="middle" className="layout-capacitor-board-positive-mark">+</text>
            <path d={`M ${bodyX + 3} ${bodyTop + 1.2} H ${bodyX + bodyWidth - 4}`} className="layout-capacitor-board-highlight" />
          </>
        ) : (
          <>
          <rect x={bodyX - 0.7} y="3" width="2.7" height={object.height - 6} rx="1" className="layout-capacitor-end-cap" />
          <rect x={bodyX + bodyWidth - 2} y="3" width="2.7" height={object.height - 6} rx="1" className="layout-capacitor-end-cap" />
          <rect
            x={bodyX + 1}
            y={object.height * 0.3}
            width={bodyWidth - 2}
            height={object.height * 0.43}
            clipPath={`url(#layout-electrolytic-clip-${object.id})`}
            className="layout-capacitor-polarity-sleeve"
          />
          {[0.2, 0.58].map(position => {
            const arrowX = bodyX + bodyWidth * position;
            return (
              <g key={position} className="layout-capacitor-polarity-arrow">
                <path d={`M ${arrowX} ${centerY} L ${arrowX + 5.2} ${centerY - 3.7} V ${centerY - 1.6} H ${arrowX + 12} V ${centerY + 1.6} H ${arrowX + 5.2} V ${centerY + 3.7} Z`} />
                <text x={arrowX + 8.6} y={centerY + 1.15} textAnchor="middle">−</text>
              </g>
            );
          })}
          <text x={bodyX + bodyWidth * 0.27} y={object.height * 0.25} textAnchor="middle" className="layout-capacitor-sleeve-marking" style={{ fill: sleeveColor.text }}>{marking}</text>
          <text x={bodyX + bodyWidth * 0.73} y={object.height * 0.25} textAnchor="middle" className="layout-capacitor-sleeve-marking" style={{ fill: sleeveColor.text }}>{marking}</text>
          <path d={`M ${bodyX + 4} 3.2 H ${bodyX + bodyWidth - 4}`} className="layout-component-highlight" />
          </>
        )
      )}
      {isMullardMustard ? (
        <g className="layout-capacitor-mustard-markings">
          <text x={object.width / 2} y={centerY - 0.25} textAnchor="middle">{object.value}</text>
          <text x={object.width / 2} y={centerY + 3.25} textAnchor="middle">{object.voltageRating || 400}V · ±10%</text>
        </g>
      ) : isSozoCoupling ? (
        <g className="layout-capacitor-sozo-markings" style={{ fontSize: sozoMarkingSize }}>
          <text x={object.width / 2} y={centerY - bodyHeight * 0.22} textAnchor="middle">NEX GEN</text>
          <text x={object.width / 2} y={centerY + bodyHeight * 0.04} textAnchor="middle">{object.value} · 500VDC</text>
          <text x={object.width / 2} y={centerY + bodyHeight * 0.32} textAnchor="middle" className="layout-capacitor-sozo-brand" style={{ fontSize: sozoBrandSize }}>SoZo</text>
        </g>
      ) : !isElectrolytic && <text x={object.width / 2 + 1} y={centerY + 1.8} textAnchor="middle" className="layout-component-value">{object.value}</text>}
    </g>
  );
};

const RadialCapacitor = ({ object }) => {
  const construction = object.construction || 'orange-drop';
  const terminals = getObjectTerminals(object);
  const isDisc = construction === 'ceramic-disc';
  const isSilverMica = construction === 'silver-mica';
  const isElectrolytic = construction === 'radial-electrolytic';
  const sleeveColor = ELECTROLYTIC_SLEEVE_COLORS.find(color => color.value === object.sleeveColor)
    || ELECTROLYTIC_SLEEVE_COLORS[2];
  const sleeveGradientId = `layout-radial-electrolytic-sleeve-${object.id}`;
  return (
    <g data-construction={construction}>
      {isElectrolytic && (
        <defs>
          <linearGradient id={sleeveGradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={sleeveColor.shadow} />
            <stop offset="0.28" stopColor={sleeveColor.highlight} />
            <stop offset="0.58" stopColor={sleeveColor.value} />
            <stop offset="1" stopColor={sleeveColor.shadow} />
          </linearGradient>
        </defs>
      )}
      {terminals.map(terminal => (
        <line key={terminal.id} x1={terminal.x} y1={object.height} x2={terminal.x} y2={object.height * (isSilverMica ? 0.31 : 0.72)} className="layout-axial-lead" />
      ))}
      {isElectrolytic ? (
        <>
          <rect x="2" y="1" width={object.width - 4} height={object.height * 0.76} rx="5" className="layout-capacitor-radial-electrolytic" style={{ fill: `url(#${sleeveGradientId})` }} />
          <ellipse cx={object.width / 2} cy="4" rx={object.width * 0.34} ry="2.2" className="layout-capacitor-radial-top" />
          <rect x={object.width * 0.69} y="2.2" width={object.width * 0.17} height={object.height * 0.69} className="layout-capacitor-radial-polarity-stripe" />
          <text x={object.width * 0.775} y={object.height * 0.35} textAnchor="middle" className="layout-capacitor-radial-minus">−</text>
          <text x={terminals[1].x} y={object.height * 0.9} textAnchor="middle" className="layout-capacitor-radial-plus">+</text>
        </>
      ) : isDisc ? (
        <ellipse cx={object.width / 2} cy={object.height * 0.4} rx={object.width * 0.42} ry={object.height * 0.34} className="layout-capacitor-ceramic-disc" />
      ) : isSilverMica ? (
        <>
          <path
            d={`M 3 1.5 Q 2 1.5 2 3 V ${object.height * 0.25} Q 2 ${object.height * 0.31} ${object.width * 0.28} ${object.height * 0.31} H ${object.width * 0.72} Q ${object.width - 2} ${object.height * 0.31} ${object.width - 2} ${object.height * 0.25} V 3 Q ${object.width - 2} 1.5 ${object.width - 3} 1.5 Z`}
            className="layout-capacitor-silver-mica"
          />
          <path d={`M 4 2.5 H ${object.width - 4}`} className="layout-capacitor-mica-highlight" />
          <text x={object.width / 2} y={object.height * 0.13} textAnchor="middle" className="layout-capacitor-mica-marking">{object.value}</text>
          <text x={object.width / 2} y={object.height * 0.22} textAnchor="middle" className="layout-capacitor-mica-marking">{object.voltageRating || 500}V</text>
        </>
      ) : (
        <rect x="1" y="1" width={object.width - 2} height={object.height * 0.7} rx="3" className="layout-capacitor-orange-drop" />
      )}
      {!isSilverMica && <text x={isElectrolytic ? object.width * 0.4 : object.width / 2} y={object.height * 0.43} textAnchor="middle" className="layout-component-value" style={isElectrolytic ? { fill: sleeveColor.text } : undefined}>{object.value}</text>}
      {isElectrolytic && <text x={object.width * 0.4} y={object.height * 0.55} textAnchor="middle" className="layout-capacitor-voltage" style={{ fill: sleeveColor.text }}>{object.voltageRating || 50} V</text>}
    </g>
  );
};

const AxialDiode = ({ object }) => {
  const construction = object.construction || 'rectifier';
  const definition = DIODE_LIBRARY[construction] || DIODE_LIBRARY.rectifier;
  const centerY = object.height / 2;
  const bodyWidth = Math.min(definition.bodyWidth, object.width - 4);
  const bodyHeight = Math.min(definition.bodyHeight, object.height - 1);
  const bodyX = (object.width - bodyWidth) / 2;
  const bodyY = centerY - bodyHeight / 2;
  const cathodeBandX = bodyX + bodyWidth * 0.76;
  return (
    <g data-construction={construction}>
      <AxialLeads object={object} />
      <rect x={bodyX} y={bodyY} width={bodyWidth} height={bodyHeight} rx={bodyHeight * 0.28} data-body-width={bodyWidth} data-body-height={bodyHeight} className={`layout-diode-body layout-diode-${construction}`} />
      {construction === 'phase-inverter' && (
        <path d={`M ${bodyX + bodyWidth * 0.2} ${centerY} H ${bodyX + bodyWidth * 0.68}`} className="layout-diode-die" />
      )}
      <rect x={cathodeBandX} y={bodyY} width={Math.max(0.8, bodyWidth * 0.12)} height={bodyHeight} className="layout-diode-cathode-band" />
      <path d={`M ${bodyX + 1.2} ${bodyY + 0.7} H ${cathodeBandX - 0.6}`} className="layout-component-highlight" />
      <text x={object.width / 2 - 0.7} y={centerY + 0.7} textAnchor="middle" className={`layout-diode-value layout-diode-value-${construction}`}>{object.value}</text>
    </g>
  );
};

const CAN_SECTION_SYMBOLS = Object.freeze([
  { value: 'semicircle', label: 'Semicircle' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'square', label: 'Square' },
  { value: 'blank', label: 'No mark' },
]);

const getCanSectionSymbol = (section, index) => (
  section?.symbol || CAN_SECTION_SYMBOLS[index]?.value || 'blank'
);

const CanTerminalMark = ({ symbol, x, y }) => {
  if (symbol === 'semicircle') {
    return <path d={`M ${x - 1.7} ${y + 1.2} A 1.7 1.7 0 0 1 ${x + 1.7} ${y + 1.2} Z`} className="layout-capacitor-can-terminal-symbol" data-symbol="semicircle" />;
  }
  if (symbol === 'triangle') {
    return <path d={`M ${x} ${y - 1.7} L ${x + 1.7} ${y + 1.3} H ${x - 1.7} Z`} className="layout-capacitor-can-terminal-symbol" data-symbol="triangle" />;
  }
  if (symbol === 'square') {
    return <rect x={x - 1.35} y={y - 1.35} width="2.7" height="2.7" className="layout-capacitor-can-terminal-symbol" data-symbol="square" />;
  }
  return null;
};

const CanCapacitor = ({ object }) => {
  const terminals = getObjectTerminals(object);
  return (
    <g data-construction="can-electrolytic">
      <circle cx={object.width / 2} cy={object.height / 2} r={Math.min(object.width, object.height) * 0.46} className="layout-capacitor-can-flange" />
      <circle cx={object.width / 2} cy={object.height / 2} r={Math.min(object.width, object.height) * 0.39} className="layout-capacitor-can" />
      <path d={`M ${object.width * 0.32} ${object.height * 0.28} L ${object.width * 0.5} ${object.height * 0.18} L ${object.width * 0.68} ${object.height * 0.28}`} className="layout-capacitor-can-vent" />
      {terminals.map(terminal => (
        <g key={terminal.id}>
          <circle cx={terminal.x} cy={terminal.y} r="2.4" className="layout-capacitor-can-terminal" />
          {terminal.id === 'common' ? (
            <text x={terminal.x} y={terminal.y + 1.15} textAnchor="middle" className="layout-capacitor-can-terminal-label">−</text>
          ) : (
            <CanTerminalMark
              symbol={getCanSectionSymbol(
                object.sections?.find(section => section.id === terminal.id),
                object.sections?.findIndex(section => section.id === terminal.id)
              )}
              x={terminal.x}
              y={terminal.y}
            />
          )}
        </g>
      ))}
      <text x={object.width / 2} y={object.height * 0.43} textAnchor="middle" className="layout-component-value layout-capacitor-can-value">{object.value}</text>
      <text x={object.width / 2} y={object.height * 0.52} textAnchor="middle" className="layout-capacitor-voltage layout-capacitor-can-voltage">{object.voltageRating || 450} V</text>
    </g>
  );
};

const Capacitor = ({ object }) => {
  if (object.packageStyle === 'radial') return <RadialCapacitor object={object} />;
  if (object.packageStyle === 'can') return <CanCapacitor object={object} />;
  return <AxialCapacitor object={object} />;
};

const Potentiometer = ({ object }) => {
  const centerX = object.width / 2;
  const isPec = object.construction === 'pec';
  if (isPec) {
    const centerY = 19;
    const bodyRadius = 14;
    const taperCode = object.taper === 'Linear' ? 'U' : object.taper === 'Reverse audio' ? 'V' : 'K';
    const terminals = getObjectTerminals(object);
    return (
      <g data-construction="pec" data-body-diameter="28" data-body-depth="13">
        {terminals.map(terminal => (
          <g key={terminal.id}>
            <rect x={terminal.x - 2.5} y="3.2" width="5" height="6.2" rx="1.5" className="layout-pot-pec-wafer-tab" />
            <path d={`M ${terminal.x - 2.1} 6.8 L ${terminal.x - 1.8} 0.8 Q ${terminal.x} -0.4 ${terminal.x + 1.8} 0.8 L ${terminal.x + 2.1} 6.8 Z`} className="layout-pot-pec-lug" />
            <rect x={terminal.x - 0.9} y="1" width="1.8" height="3.1" rx="0.9" className="layout-pot-pec-lug-hole" />
          </g>
        ))}
        <circle cx={centerX} cy={centerY} r={bodyRadius} className="layout-pot-pec-body" />
        <circle cx={centerX} cy={centerY} r="12.8" className="layout-pot-pec-rim" />
        <path d={`M ${centerX - 9.2} ${centerY - 8.2} A 12.2 12.2 0 0 1 ${centerX + 9.2} ${centerY - 8.2}`} className="layout-pot-pec-highlight" />
        <g className="layout-pot-pec-markings">
          <rect x={centerX - 3.8} y={centerY - 7.4} width="7.6" height="5.2" className="layout-pot-pec-logo-box" />
          <path d={`M ${centerX - 2.7} ${centerY - 3.1} V ${centerY - 6.5} L ${centerX - 1.4} ${centerY - 4.2} L ${centerX} ${centerY - 6.5} L ${centerX + 1.4} ${centerY - 4.2} L ${centerX + 2.7} ${centerY - 6.5} V ${centerY - 3.1}`} />
          <text x={centerX} y={centerY + 0.1} textAnchor="middle">RV4</text>
          <text x={centerX} y={centerY + 3.4} textAnchor="middle" className="layout-pot-pec-rating">{object.value}</text>
          <text x={centerX} y={centerY + 6.2} textAnchor="middle" className="layout-pot-pec-taper">TYPE {taperCode}</text>
        </g>
      </g>
    );
  }
  const centerY = object.height * 0.62;
  const bodyRadius = Math.min(object.width * 0.31, object.height * 0.35);
  const terminals = getObjectTerminals(object);
  return (
    <g>
      <rect x={centerX - bodyRadius * 1.15} y={centerY - bodyRadius * 1.12} width={bodyRadius * 2.3} height={bodyRadius * 0.52} rx="1" className="layout-pot-wafer" />
      {terminals.map(terminal => (
        <g key={terminal.id}>
          <path d={`M ${terminal.x - 2.1} ${centerY - bodyRadius * 0.76} L ${terminal.x - 2.7} ${terminal.y + 1} L ${terminal.x + 2.7} ${terminal.y + 1} L ${terminal.x + 2.1} ${centerY - bodyRadius * 0.76} Z`} className="layout-hardware-lug" />
          <circle cx={terminal.x} cy={terminal.y + 3} r="1.15" className="layout-lug-hole" />
        </g>
      ))}
      <circle cx={centerX} cy={centerY} r={bodyRadius} className="layout-pot-body" />
      <circle cx={centerX} cy={centerY} r={bodyRadius * 0.88} className="layout-pot-can-face" />
      <path d={`M ${centerX - bodyRadius * 0.68} ${centerY - bodyRadius * 0.68} A ${bodyRadius * 0.96} ${bodyRadius * 0.96} 0 0 1 ${centerX + bodyRadius * 0.68} ${centerY - bodyRadius * 0.68}`} className="layout-pot-can-highlight" />
      <text x={centerX} y={centerY - 0.4} textAnchor="middle" className="layout-component-value layout-pot-value">{object.value}</text>
      <text x={centerX} y={centerY + 3.3} textAnchor="middle" className="layout-pot-taper">{object.taper === 'Audio' ? 'A' : 'B'}</text>
    </g>
  );
};

const InputJack = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const bodyLeft = object.width * 0.2;
  const bodyRight = object.width * 0.8;
  const bodyTop = object.height * 0.15;
  const bodyBottom = object.height * 0.88;
  return (
    <g>
      <rect x={object.width * 0.34} y="0" width={object.width * 0.32} height={object.height * 0.2} rx="1" className="layout-jack-bushing" />
      <rect x={object.width * 0.27} y={object.height * 0.08} width={object.width * 0.46} height={object.height * 0.12} rx="1" className="layout-jack-collar" />
      <path d={`M ${bodyLeft} ${bodyTop} H ${bodyRight} V ${bodyBottom} H ${bodyLeft} Z`} className="layout-jack-body" />
      <rect x={bodyLeft - 1.2} y={object.height * 0.28} width={bodyRight - bodyLeft + 2.4} height={object.height * 0.13} rx="1" className="layout-jack-contact-block" />
      <rect x={bodyLeft - 1.2} y={object.height * 0.62} width={bodyRight - bodyLeft + 2.4} height={object.height * 0.13} rx="1" className="layout-jack-contact-block" />
      <rect x={object.width * 0.34} y={bodyBottom - 0.5} width={object.width * 0.32} height={object.height * 0.12} rx="1" className="layout-jack-foot" />
      <text x={object.width / 2} y={object.height * 0.36} textAnchor="middle" className="layout-jack-terminal-label">SLEEVE</text>
      <text x={object.width / 2} y={object.height * 0.7} textAnchor="middle" className="layout-jack-terminal-label">TIP</text>
      {terminals.map(terminal => (
        <g key={terminal.id}>
          <line x1={terminal.x < object.width / 2 ? bodyLeft : bodyRight} y1={terminal.y} x2={terminal.x} y2={terminal.y} className="layout-jack-terminal-strip" />
          <circle cx={terminal.x} cy={terminal.y} r="2.2" className="layout-jack-side-lug" />
          <circle cx={terminal.x} cy={terminal.y} r="0.85" className="layout-lug-hole" />
        </g>
      ))}
    </g>
  );
};

const MAGNETIC_LEAD_COLORS = Object.freeze({
  'auxiliary-6v3': '#c9cecc',
  'rectifier-5v': '#f1bd32',
  'auxiliary-common': '#596366',
  'high-voltage-1': '#ef473d',
  'high-voltage-center-tap': '#f1bd32',
  'high-voltage-2': '#ef473d',
  'primary-240v': '#a85a32',
  'primary-120v': '#ef473d',
  'primary-common': '#596366',
  'heater-1': '#35ad6f',
  'heater-center-tap': '#35ad6f',
  'heater-2': '#35ad6f',
  'plate-1': '#4385e2',
  'primary-center-tap': '#ef473d',
  'plate-2': '#a85a32',
  'secondary-common': '#596366',
  'secondary-4-ohm': '#f1bd32',
  'secondary-8-ohm': '#35ad6f',
  'secondary-16-ohm': '#ff8a24',
  'lead-1': '#ef473d',
  'lead-2': '#ef473d',
});

const MagneticComponent = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const isLaydown = object.mountStyle === 'laydown';

  if (isLaydown) {
    const frameInset = 4;
    const paperX = object.width * 0.13;
    const paperY = object.height * 0.17;
    const paperWidth = object.width * 0.74;
    const paperHeight = object.height * 0.66;
    return (
      <g>
        <rect x="1" y="1" width={object.width - 2} height={object.height - 2} rx="2" className="layout-transformer-laydown-frame" />
        {[[frameInset, frameInset], [object.width - frameInset, frameInset], [frameInset, object.height - frameInset], [object.width - frameInset, object.height - frameInset]].map(([cx, cy]) => (
          <g key={`${cx}-${cy}`}>
            <circle cx={cx} cy={cy} r="3" className="layout-mount-ring" />
            <circle cx={cx} cy={cy} r="1.4" className="layout-mount-hole" />
          </g>
        ))}
        <rect x={paperX} y={paperY} width={paperWidth} height={paperHeight} rx="2" className="layout-transformer-winding-paper" />
        <rect x={paperX + 2} y={paperY + 2} width={paperWidth - 4} height={paperHeight - 4} rx="1" className="layout-transformer-winding-inset" />
        {terminals.map(terminal => {
          const bodyY = terminal.y === 0 ? paperY : paperY + paperHeight;
          const labelY = terminal.y === 0 ? paperY + 5 : paperY + paperHeight - 5;
          return (
            <g key={terminal.id}>
              <line x1={terminal.x} y1={terminal.y} x2={terminal.x} y2={bodyY} stroke={terminal.color || MAGNETIC_LEAD_COLORS[terminal.id] || BRIGHT_WIRE_COLORS[4]} className="layout-transformer-lead" />
              <rect x={terminal.x - 2.1} y={bodyY - 1.6} width="4.2" height="3.2" rx="0.7" className="layout-transformer-lead-tab" />
              <text x={terminal.x} y={labelY} textAnchor="middle" className="layout-transformer-specification">{terminal.specification}</text>
            </g>
          );
        })}
        <text x={object.width / 2} y={object.height / 2 - 1} textAnchor="middle" className="layout-transformer-paper-title">{object.label}</text>
        <text x={object.width / 2} y={object.height / 2 + 5} textAnchor="middle" className="layout-transformer-paper-subtitle">{object.value}</text>
      </g>
    );
  }

  const leftHole = { x: object.width * 0.19, y: object.height / 2 };
  const rightHole = { x: object.width * 0.81, y: object.height / 2 };

  return (
    <g>
      <rect x="1" y={object.height * 0.16} width={object.width - 2} height={object.height * 0.68} rx="2" className="layout-transformer-foot" />
      {[4, object.width - 4].map(cx => (
        <g key={cx}>
          <circle cx={cx} cy={object.height / 2} r="2.8" className="layout-mount-ring" />
          <circle cx={cx} cy={object.height / 2} r="1.35" className="layout-mount-hole" />
        </g>
      ))}
      {terminals.map(terminal => {
        const hole = terminal.x === 0 ? leftHole : rightHole;
        return (
          <path key={`lead-${terminal.id}`} d={`M ${hole.x} ${hole.y} Q ${(hole.x + terminal.x) / 2} ${terminal.y}, ${terminal.x} ${terminal.y}`} stroke={terminal.color || MAGNETIC_LEAD_COLORS[terminal.id] || BRIGHT_WIRE_COLORS[4]} className="layout-transformer-lead" />
        );
      })}
      <rect
        x={object.width * 0.16}
        y={object.height * 0.1}
        width={object.width * 0.68}
        height={object.height * 0.8}
        rx={object.type === 'choke' ? 5 : 8}
        className="layout-transformer-bell"
      />
      <path
        d={`M ${object.width * 0.28} ${object.height * 0.28} H ${object.width * 0.72}`}
        className="layout-transformer-highlight"
      />
      {[leftHole, rightHole].map(hole => (
        <g key={hole.x}>
          <circle cx={hole.x} cy={hole.y} r="4.6" className="layout-transformer-grommet" />
          <circle cx={hole.x} cy={hole.y} r="2.6" className="layout-transformer-exit-hole" />
        </g>
      ))}
      <text x={object.width / 2} y={object.height * 0.45} textAnchor="middle" className="layout-transformer-bell-title">{object.label}</text>
      {object.type === 'output-transformer' && (
        <text x={object.width / 2} y={object.height * 0.58} textAnchor="middle" className="layout-transformer-bell-spec">COM · 4Ω · 8Ω · 16Ω</text>
      )}
    </g>
  );
};

const ParallelOutputJacks = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const leftX = object.width * 0.22;
  const rightX = object.width * 0.78;
  return (
    <g>
      <line x1={leftX} y1={object.height * 0.22} x2={rightX} y2={object.height * 0.22} className="layout-output-jack-strap" />
      <line x1={leftX} y1={object.height * 0.78} x2={rightX} y2={object.height * 0.78} className="layout-output-jack-strap" />
      {[leftX, rightX].map((centerX, index) => (
        <g key={centerX}>
          <rect x={centerX - 8} y="3" width="16" height={object.height - 6} rx="4" className="layout-output-jack-body" />
          <rect x={centerX - 10} y={object.height * 0.16} width="20" height="6" rx="1" className="layout-output-jack-bracket" />
          <rect x={centerX - 10} y={object.height * 0.68} width="20" height="6" rx="1" className="layout-output-jack-bracket" />
          <circle cx={centerX} cy={object.height * 0.45} r="4.5" className="layout-output-jack-well" />
          <text x={centerX} y={object.height * 0.49} textAnchor="middle" className="layout-output-jack-number">{index + 1}</text>
        </g>
      ))}
      {terminals.map(terminal => (
        <g key={terminal.id}>
          <circle cx={terminal.x} cy={terminal.y} r="2.3" className="layout-jack-side-lug" />
          <circle cx={terminal.x} cy={terminal.y} r="0.85" className="layout-lug-hole" />
        </g>
      ))}
      <text x={object.width / 2} y={object.height * 0.14} textAnchor="middle" className="layout-output-jack-contact-label">SLEEVE</text>
      <text x={object.width / 2} y={object.height * 0.94} textAnchor="middle" className="layout-output-jack-contact-label">TIP</text>
    </g>
  );
};

const ImpedanceSelector = ({ object }) => {
  const centerX = object.width / 2;
  const centerY = 21;
  const terminals = getObjectTerminals(object);
  const lugBlades = [
    'M 12.7 16 L 1.8 9.2 Q 0.6 7.5 1.7 5.4 Q 3.8 2.2 6.3 4.2 L 15.2 11.7 Z',
    'M 16.1 12.6 L 16.1 2.3 Q 16.1 -0.7 19 -2 Q 21.9 -0.7 21.9 2.3 L 21.9 12.6 Z',
    'M 22.8 12.1 L 30.6 3.9 Q 32.5 1.8 34.8 3.5 Q 37.4 5.5 35.7 8 L 27.3 16 Z',
    'M 25.5 17.2 L 35.9 16.6 Q 38.8 16.5 40 19.2 Q 40.9 22.2 38 23.5 L 25.5 23.5 Z',
  ];
  return (
    <g data-construction="rotary-impedance-selector">
      <circle cx={centerX} cy={centerY} r="15.5" className="layout-impedance-wafer" />
      <circle cx={centerX} cy={centerY} r="12.8" className="layout-impedance-wafer-ring" />
      {[0, 60, 120, 180, 240, 300].map(angle => (
        <path key={angle} d="M 16.2 5.8 L 21.8 5.8 L 21 12.4 L 17 12.4 Z" transform={`rotate(${angle} ${centerX} ${centerY})`} className="layout-impedance-contact-block" />
      ))}
      {terminals.map((terminal, index) => (
        <g key={terminal.id}>
          <path d={lugBlades[index]} className="layout-impedance-lug" data-terminal-id={terminal.id} />
          <circle cx={terminal.x} cy={terminal.y} r="3.4" className="layout-impedance-lug-eyelet" />
          <ellipse cx={terminal.x} cy={terminal.y} rx="1.15" ry="1.65" className="layout-impedance-lug-hole" />
        </g>
      ))}
      <path d="M 11.1 12.5 L 15.4 9.2 L 21 16 L 16.9 19 Z" className="layout-impedance-index-arm" />
      <path d="M 22 15.2 L 28.4 10.8 L 30.5 14.7 L 24.1 19 Z" className="layout-impedance-support-arm" />
      <path d="M 15.7 22 L 9.5 29 L 12.7 31.6 L 19.2 24.2 Z" className="layout-impedance-support-arm" />
      <path d="M 24.8 19.8 L 33.5 19.8 L 33.5 23.4 L 24.8 23.4 Z" className="layout-impedance-stop-arm" />
      <circle cx="33.2" cy="21.8" r="1.1" className="layout-impedance-rivet" />
      <circle cx="19" cy="19" r="7.1" className="layout-impedance-bracket" />
      <circle cx="19" cy="19" r="5.5" className="layout-impedance-nut" />
      <path d="M 14.5 15.3 Q 19 12.5 23.5 15.3 L 22.8 22.9 Q 19 25.6 15.2 22.9 Z" className="layout-impedance-retainer" />
      <circle cx="19" cy="19" r="3.8" className="layout-impedance-bushing" />
      <path d="M 16.2 16.3 Q 19 14.6 21.8 16.3 L 22.1 20.2 L 19 22.4 L 15.9 20.2 Z" className="layout-impedance-shaft" />
      <circle cx="25.5" cy="35.2" r="2" className="layout-impedance-stop-pin" />
      <circle cx="25.5" cy="35.2" r="0.9" className="layout-impedance-stop-pin-hole" />
    </g>
  );
};

const CarlingToggleSwitch = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const topology = object.construction.toUpperCase();
  return (
    <g data-construction={`carling-${object.construction}`} data-view="rear">
      <rect x="3" y="2" width="28" height="27" rx="2" className="layout-carling-housing" />
      <path d="M 2 4 L 4 1 H 30 L 32 4 V 7 H 2 Z" className="layout-carling-mounting-cap" />
      <rect x="5" y="7" width="24" height="17.5" rx="1" className="layout-carling-back-panel" />
      <path d="M 7 9 H 27 M 7 22 H 27" className="layout-carling-molded-ridge" />
      <text x="17" y="13" textAnchor="middle" className="layout-carling-brand">CARLING</text>
      <text x="17" y="17" textAnchor="middle" className="layout-carling-topology">{topology}</text>
      <text x="17" y="20.5" textAnchor="middle" className="layout-carling-rating">15A 250VAC</text>
      {terminals.map(terminal => (
        <g key={terminal.id} className="layout-carling-terminal" data-terminal-id={terminal.id}>
          <path d={`M ${terminal.x - 2.5} ${terminal.y - 6} H ${terminal.x + 2.5} L ${terminal.x + 2.1} ${terminal.y + 2.8} H ${terminal.x - 2.1} Z`} className="layout-carling-terminal-tab" />
          <circle cx={terminal.x} cy={terminal.y - 4.1} r="2.1" className="layout-carling-terminal-screw" />
          <path d={`M ${terminal.x - 1.15} ${terminal.y - 4.1} H ${terminal.x + 1.15}`} className="layout-carling-screw-slot" />
          <circle cx={terminal.x} cy={terminal.y} r="1.05" className="layout-carling-terminal-hole" />
        </g>
      ))}
    </g>
  );
};

const IecPowerInlet = ({ object }) => {
  const terminals = getObjectTerminals(object);
  const terminalBlades = {
    'protective-earth': 'M 19.5 13.5 V 4.5 Q 19.5 2.3 22 2.3 Q 24.5 2.3 24.5 4.5 V 13.5 Z',
    neutral: 'M 11.5 24 V 15.5 Q 11.5 13.5 14 13.5 Q 16.5 13.5 16.5 15.5 V 24 Z',
    line: 'M 27.5 24 V 15.5 Q 27.5 13.5 30 13.5 Q 32.5 13.5 32.5 15.5 V 24 Z',
  };
  return (
    <g data-construction="iec-c14-power-inlet" data-view="rear">
      <path d="M 2 8 L 8 3 H 36 L 42 8 V 27 L 36 30 H 8 L 2 27 Z" className="layout-iec-flange" />
      <circle cx="5.5" cy="19" r="2.7" className="layout-iec-mount-hole" />
      <circle cx="38.5" cy="19" r="2.7" className="layout-iec-mount-hole" />
      <path d="M 9 5 H 35 L 38 9 V 27 H 6 V 9 Z" className="layout-iec-housing" />
      <path d="M 11 7 H 33 L 35.5 10 V 25 H 8.5 V 10 Z" className="layout-iec-back-panel" />
      <path d="M 10.5 9 H 33.5 M 9.5 23.5 H 34.5" className="layout-iec-molded-ridge" />
      {terminals.map(terminal => (
        <g key={terminal.id} className="layout-iec-terminal" data-terminal-id={terminal.id}>
          <path d={terminalBlades[terminal.id]} className="layout-iec-terminal-blade" />
          <ellipse cx={terminal.x} cy={terminal.y} rx="1.15" ry="1.45" className="layout-iec-terminal-hole" />
        </g>
      ))}
      <text x="14" y="12.4" textAnchor="middle" className="layout-iec-terminal-label">N</text>
      <g transform="translate(22 16.5)" className="layout-iec-earth-mark">
        <path d="M 0 -2.4 V 0.8 M -2 0.8 H 2 M -1.4 1.7 H 1.4 M -0.7 2.6 H 0.7" />
      </g>
      <text x="30" y="12.4" textAnchor="middle" className="layout-iec-terminal-label">L</text>
      <text x="22" y="27.2" textAnchor="middle" className="layout-iec-model">IEC C14</text>
    </g>
  );
};

const PanelFuseHolder = ({ object }) => {
  const terminals = getObjectTerminals(object);
  return (
    <g data-construction="panel-fuse-holder" data-view="rear">
      <path d="M 9 7.2 L 13 3 H 21 L 25 7.2 L 27.5 14 L 25 20.8 L 21 25 H 13 L 9 20.8 L 6.5 14 Z" className="layout-fuse-holder-nut" />
      <circle cx="17" cy="14" r="9.2" className="layout-fuse-holder-threaded-body" />
      {[0, 30, 60, 90, 120, 150].map(angle => (
        <path key={angle} d="M 17 5.8 V 8.1" transform={`rotate(${angle} 17 14)`} className="layout-fuse-holder-thread" />
      ))}
      <circle cx="17" cy="14" r="6.7" className="layout-fuse-holder-rear-cap" />
      {[0, 30, 60, 90, 120, 150].map(angle => (
        <path key={angle} d="M 17 7.9 V 10.1" transform={`rotate(${angle} 17 14)`} className="layout-fuse-holder-cap-rib" />
      ))}
      <path d="M 8.8 8.5 L 1.8 8.5 Q -0.2 8.5 -0.2 11 Q -0.2 13.5 1.8 13.5 H 9.7 Z" className="layout-fuse-holder-terminal-blade" data-terminal-id="side-contact" />
      <ellipse cx="0" cy="11" rx="1.15" ry="1.45" className="layout-fuse-holder-terminal-hole" />
      <path d="M 14.5 19.2 V 35 Q 14.5 38 17 39 Q 19.5 38 19.5 35 V 19.2 Z" className="layout-fuse-holder-terminal-blade" data-terminal-id="center-contact" />
      <ellipse cx="17" cy="38" rx="1.15" ry="1.45" className="layout-fuse-holder-terminal-hole" />
      <circle cx="17" cy="14" r="3.2" className="layout-fuse-holder-contact-disc" />
      <text x="17" y="15" textAnchor="middle" className="layout-fuse-holder-marking">FUSE</text>
      {terminals.map(terminal => (
        <circle key={terminal.id} cx={terminal.x} cy={terminal.y} r="0.01" className="layout-fuse-holder-contact-anchor" />
      ))}
    </g>
  );
};

const getObjectAccessibleName = object => object.label || ({
  resistor: 'Resistor',
  capacitor: 'Capacitor',
  turret: 'Turret',
}[object.type] || 'Component');

const LayoutObject = ({ object, resistorDisplay, selected, wireMode, showTerminalTargets, pendingEndpoint, onPointerDown, onTerminalPointerDown, onLeadHandlePointerDown }) => (
  <g
    transform={`translate(${object.x} ${object.y}) rotate(${object.rotation || 0} ${object.width / 2} ${object.height / 2})`}
    className={`layout-object${selected ? ' is-selected' : ''}`}
    onPointerDown={(event) => onPointerDown(event, object)}
    data-layout-object-id={object.id}
    data-testid={`layout-object-${object.type}`}
    data-scene-item={`object:${object.id}`}
  >
    {object.type === 'chassis' && <Chassis object={object} />}
    {object.type === 'turret-board' && <TurretBoard object={object} />}
    {object.type === 'blank-turret-board' && <BlankTurretBoard object={object} />}
    {object.type === 'turret' && <StandaloneTurret object={object} />}
    {(object.type === 'tube-socket' || object.type === 'octal-socket') && <TubeSocket object={object} />}
    {object.type === 'resistor' && <AxialResistor object={object} displayMode={resistorDisplay} />}
    {object.type === 'capacitor' && <Capacitor object={object} />}
    {object.type === 'diode' && <AxialDiode object={object} />}
    {object.type === 'potentiometer' && <Potentiometer object={object} />}
    {object.type === 'impedance-selector' && <ImpedanceSelector object={object} />}
    {object.type === 'carling-toggle-switch' && <CarlingToggleSwitch object={object} />}
    {object.type === 'iec-power-inlet' && <IecPowerInlet object={object} />}
    {object.type === 'panel-fuse-holder' && <PanelFuseHolder object={object} />}
    {object.type === 'input-jack' && <InputJack object={object} />}
    {object.type === 'parallel-output-jacks' && <ParallelOutputJacks object={object} />}
    {(object.type === 'power-transformer' || object.type === 'output-transformer' || object.type === 'choke') && <MagneticComponent object={object} />}
    {getObjectTerminals(object).map(terminal => {
      const isPending = pendingEndpoint?.objectId === object.id && pendingEndpoint?.terminalId === terminal.id;
      return (
        <circle
          key={`terminal-${terminal.id}`}
          cx={terminal.x}
          cy={terminal.y}
          r={wireMode || showTerminalTargets ? 4.5 : 3}
          className={`layout-terminal-hit${wireMode || showTerminalTargets ? ' is-wirable' : ''}${isPending ? ' is-pending' : ''}`}
          data-layout-export-exclude="true"
          aria-label={`${getObjectAccessibleName(object)}, ${terminal.label}`}
          onPointerDown={(event) => onTerminalPointerDown(event, object, terminal)}
        />
      );
    })}
    {selected && !wireMode && getAxialLeadDefinitions(object).map(lead => {
      const terminal = getObjectTerminals(object).find(item => item.id === lead.terminalId);
      return (
        <g key={`lead-handles-${lead.terminalId}`} data-layout-export-exclude="true">
          <rect
            x={lead.hinge.x - 2.5}
            y={lead.hinge.y - 2.5}
            width="5"
            height="5"
            className="layout-lead-handle layout-lead-hinge-handle"
            aria-label={`${getObjectAccessibleName(object)}, ${terminal.label} hinge`}
            onPointerDown={(event) => onLeadHandlePointerDown(event, object, lead, 'hinge')}
          />
          <circle
            cx={lead.terminal.x}
            cy={lead.terminal.y}
            r="3"
            className="layout-lead-handle layout-lead-terminal-handle"
            aria-label={`${getObjectAccessibleName(object)}, ${terminal.label} endpoint`}
            onPointerDown={(event) => onLeadHandlePointerDown(event, object, lead, 'terminal')}
          />
        </g>
      );
    })}
    <text x={object.width / 2} y={-5} textAnchor="middle" className="layout-object-label">
      {object.label}
    </text>
    {selected && (
      <rect
        x="-2"
        y="-2"
        width={object.width + 4}
        height={object.height + 4}
        rx="3"
        className="layout-selection-outline"
        data-layout-export-exclude="true"
      />
    )}
  </g>
);

const LayoutConductor = ({ conductor, document, partner, selected, onPointerDown }) => {
  const from = resolveTerminalPosition(document, conductor.from);
  const to = resolveTerminalPosition(document, conductor.to);
  if (!from || !to) return null;
  const { control1, control2 } = getConductorCurveControls(conductor, from, to);
  const braidedPoints = partner ? getBraidedConductorPoints(document, conductor, partner) : [];
  const path = braidedPoints.length > 0
    ? braidedPoints.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
    : `M ${from.x} ${from.y} C ${control1.x} ${control1.y}, ${control2.x} ${control2.y}, ${to.x} ${to.y}`;
  const baseStrokeWidth = getWireStrokeWidth(conductor.gauge);
  const strokeWidth = baseStrokeWidth + (selected ? 0.8 : 0);
  return (
    <g
      className={`layout-conductor${selected ? ' is-selected' : ''}`}
      data-scene-item={`conductor:${conductor.id}`}
    >
      <path d={path} className="layout-conductor-hit" data-layout-export-exclude="true" aria-label="Layout wire" onPointerDown={onPointerDown} />
      <path
        d={path}
        stroke={conductor.color}
        strokeWidth={strokeWidth}
        className="layout-conductor-line"
        data-layout-export-stroke-width={baseStrokeWidth}
        data-testid="layout-conductor"
      />
    </g>
  );
};

const StackControls = ({ onReorder }) => (
  <div className="layout-field">
    <span>Stacking order</span>
    <div className="layout-stack-controls">
      <button type="button" onClick={() => onReorder('front')}>Bring to front</button>
      <button type="button" onClick={() => onReorder('forward')}>Bring forward</button>
      <button type="button" onClick={() => onReorder('backward')}>Send backward</button>
      <button type="button" onClick={() => onReorder('back')}>Send to back</button>
    </div>
  </div>
);

const normalizeRotation = (value) => ((value % 360) + 360) % 360;

const ObjectContextMenu = ({ menu, object, selectedCount, onRotate, onReorder, onDuplicate, onDelete, onClose }) => {
  const [rotationDraft, setRotationDraft] = useState(String(object.rotation || 0));

  useEffect(() => {
    setRotationDraft(String(object.rotation || 0));
  }, [object.id, object.rotation]);

  const commitRotation = () => {
    const rotation = Number(rotationDraft);
    if (!Number.isFinite(rotation)) {
      setRotationDraft(String(object.rotation || 0));
      return;
    }
    const normalized = normalizeRotation(rotation);
    onRotate(normalized);
    setRotationDraft(String(normalized));
  };

  return (
    <div
      className="layout-context-menu"
      role="menu"
      aria-label="Object actions"
      style={{ left: menu.x, top: menu.y }}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="layout-context-menu-title">{selectedCount > 1 ? `${selectedCount} objects` : getObjectAccessibleName(object)}</div>
      <div className="layout-context-section">
        <span>Rotation</span>
        <div className="layout-context-rotation-presets">
          {[0, 90, 180, 270].map(rotation => (
            <button
              key={rotation}
              type="button"
              className={normalizeRotation(object.rotation || 0) === rotation ? 'is-active' : ''}
              onClick={() => onRotate(rotation)}
            >
              {rotation}°
            </button>
          ))}
        </div>
        <label className="layout-context-angle">
          <span>Exact angle</span>
          <div>
            <input
              type="number"
              step="1"
              value={rotationDraft}
              aria-label="Exact rotation degrees"
              onChange={(event) => setRotationDraft(event.target.value)}
              onBlur={commitRotation}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitRotation();
                }
                if (event.key === 'Escape') onClose();
              }}
            />
            <span>°</span>
          </div>
        </label>
      </div>
      <div className="layout-context-section layout-context-grid">
        <button type="button" onClick={() => onReorder('front')}>Bring to front</button>
        <button type="button" onClick={() => onReorder('forward')}>Bring forward</button>
        <button type="button" onClick={() => onReorder('backward')}>Send backward</button>
        <button type="button" onClick={() => onReorder('back')}>Send to back</button>
      </div>
      <div className="layout-context-section layout-context-grid">
        <button type="button" onClick={onDuplicate}>{selectedCount > 1 ? 'Duplicate objects' : 'Duplicate object'}</button>
        <button type="button" className="is-danger" onClick={onDelete}>{selectedCount > 1 ? 'Delete objects' : 'Delete object'}</button>
      </div>
    </div>
  );
};

const NumberField = ({ label, value, units, min = 0, onChange }) => {
  const [draft, setDraft] = useState(() => formatMeasurement(value, units));

  useEffect(() => {
    setDraft(formatMeasurement(value, units));
  }, [units, value]);

  const commitDraft = () => {
    const millimeters = parseMeasurement(draft, units);
    if (millimeters !== null && millimeters >= min) {
      onChange(millimeters);
      setDraft(formatMeasurement(millimeters, units));
    } else {
      setDraft(formatMeasurement(value, units));
    }
  };

  return (
    <label className="layout-field">
      <span>{label}</span>
      <div className="layout-number-input">
        <input
          type="text"
          inputMode="decimal"
          aria-label={`${label} ${units}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitDraft();
            }
          }}
        />
        <span>{units}</span>
      </div>
    </label>
  );
};

const GridField = ({ value, units, onChange }) => {
  const [draft, setDraft] = useState(() => formatMeasurement(value, units));

  useEffect(() => {
    setDraft(formatMeasurement(value, units));
  }, [units, value]);

  const commitDraft = () => {
    const millimeters = parseMeasurement(draft, units);
    if (millimeters !== null && millimeters >= 0.5) {
      onChange(millimeters);
      setDraft(formatMeasurement(millimeters, units));
    } else {
      setDraft(formatMeasurement(value, units));
    }
  };

  return (
    <label className="layout-grid-control">
      <span>Grid</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`Grid spacing ${units}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
          }
        }}
      />
      <span>{units}</span>
    </label>
  );
};

const PowerTransformerTapEditor = ({ taps, onChange, onAdd, onRemove }) => (
  <div className="layout-transformer-taps">
    {['top', 'bottom'].map(side => (
      <section key={side} className="layout-transformer-tap-side">
        <header>
          <span>{side === 'top' ? 'Top taps' : 'Bottom taps'}</span>
          <button type="button" aria-label={`Add ${side} tap`} onClick={() => onAdd(side)}>+ Add</button>
        </header>
        {taps[side].length === 0 ? (
          <p>No taps on this side.</p>
        ) : taps[side].map((tap, index) => (
          <div key={tap.id} className="layout-transformer-tap-row">
            <input
              aria-label={`${side} tap ${index + 1} label`}
              value={tap.label}
              onChange={(event) => onChange(side, tap.id, { label: event.target.value })}
              placeholder="Winding"
            />
            <input
              aria-label={`${side} tap ${index + 1} voltage`}
              value={tap.specification}
              onChange={(event) => onChange(side, tap.id, { specification: event.target.value })}
              placeholder="V"
            />
            <input
              type="color"
              aria-label={`${side} tap ${index + 1} color`}
              value={tap.color || BRIGHT_WIRE_COLORS[4]}
              onChange={(event) => onChange(side, tap.id, { color: event.target.value })}
            />
            <button type="button" aria-label={`Remove ${side} tap ${index + 1}`} onClick={() => onRemove(side, tap.id)}>×</button>
          </div>
        ))}
      </section>
    ))}
  </div>
);

const CanCapacitorSectionEditor = ({ sections, onChange, onAdd, onRemove }) => (
  <div className="layout-can-sections">
    <header>
      <span>Can sections</span>
      <button type="button" onClick={onAdd} disabled={sections.length >= 4}>+ Add</button>
    </header>
    {sections.map(section => (
      <div key={section.id} className="layout-can-section-row">
        <label>
          <span>{section.label}</span>
          <input
            aria-label={`${section.label} capacitance`}
            value={section.capacitance}
            onChange={(event) => onChange(section.id, { capacitance: event.target.value })}
          />
        </label>
        <label>
          <span>Mark</span>
          <select
            aria-label={`${section.label} symbol`}
            value={getCanSectionSymbol(section, sections.indexOf(section))}
            onChange={(event) => onChange(section.id, { symbol: event.target.value })}
          >
            {CAN_SECTION_SYMBOLS.map(symbol => <option key={symbol.value} value={symbol.value}>{symbol.label}</option>)}
          </select>
        </label>
        <button type="button" aria-label={`Remove ${section.label}`} disabled={sections.length <= 1} onClick={() => onRemove(section.id)}>×</button>
      </div>
    ))}
  </div>
);

const COMPONENT_FAMILIES = [
  {
    id: 'structure',
    label: 'Structure',
    items: [
      { type: 'chassis', name: 'Chassis', addLabel: 'Add chassis', description: 'Sheet-metal enclosure', iconClass: 'layout-part-icon-chassis', tags: 'cabinet enclosure metal' },
      { type: 'turret-board', name: 'Turret board', addLabel: 'Add turret board', description: 'Configurable terminal grid', iconClass: 'layout-part-icon-board', tags: 'eyelet terminal strip' },
      { type: 'blank-turret-board', name: 'Blank turret board', addLabel: 'Add blank turret board', description: 'Unpopulated board substrate', iconClass: 'layout-part-icon-board', tags: 'custom terminal strip substrate' },
      { type: 'turret', name: 'Turret', addLabel: 'Add individual turret', description: 'Freely positioned wire terminal', iconClass: 'layout-part-icon-turret', tags: 'individual custom terminal post' },
    ],
  },
  {
    id: 'tube-hardware',
    label: 'Tube hardware',
    items: [
      { type: 'tube-socket', name: '9-pin socket', addLabel: 'Add 9-pin socket', description: 'Noval chassis socket', iconClass: 'layout-part-icon-socket', tags: 'preamp valve miniature' },
      { type: 'tube-socket', construction: 'phenolic-noval', name: 'Phenolic 9-pin noval socket', addLabel: 'Add phenolic 9-pin noval socket', description: 'Vintage 12AX7 / ECC83 socket', iconClass: 'layout-part-icon-socket layout-part-icon-phenolic-noval', tags: '12ax7 ecc83 noval preamp tube valve brown phenolic vintage' },
      { type: 'octal-socket', name: '8-pin socket', addLabel: 'Add 8-pin socket', description: 'Octal chassis socket', iconClass: 'layout-part-icon-socket layout-part-icon-octal', tags: 'power tube valve' },
      { type: 'octal-socket', construction: 'mcmurdo', name: 'McMurdo octal socket', addLabel: 'Add McMurdo octal socket', description: 'Vintage phenolic EL34 socket', iconClass: 'layout-part-icon-socket layout-part-icon-mcmurdo', tags: 'mcmurdo el34 kt66 kt88 6l6 power tube valve british vintage' },
    ],
  },
  {
    id: 'magnetics',
    label: 'Transformers & magnetics',
    items: [
      { type: 'power-transformer', name: 'Power transformer', addLabel: 'Add power transformer', description: 'Mains, high-voltage, and heater windings', iconClass: 'layout-part-icon-transformer', tags: 'pt mains power supply iron magnetic' },
      { type: 'output-transformer', name: 'Output transformer', addLabel: 'Add output transformer', description: 'Push-pull primary with speaker taps', iconClass: 'layout-part-icon-transformer layout-part-icon-output-transformer', tags: 'ot speaker impedance plate iron magnetic' },
      { type: 'choke', name: 'Filter choke', addLabel: 'Add filter choke', description: 'Power-supply smoothing inductor', iconClass: 'layout-part-icon-transformer layout-part-icon-choke', tags: 'inductor power supply filter magnetic' },
    ],
  },
  {
    id: 'controls-connectors',
    label: 'Controls & connectors',
    items: [
      { type: 'potentiometer', name: 'Potentiometer', addLabel: 'Add potentiometer', description: '3-lug panel control', iconClass: 'layout-part-icon-pot', tags: 'pot volume tone variable resistor' },
      { type: 'potentiometer', construction: 'pec', name: 'PEC potentiometer', addLabel: 'Add PEC potentiometer', description: 'RV4 sealed 3-lug control', iconClass: 'layout-part-icon-pot layout-part-icon-pec-pot', tags: 'pec rv4 mil spec sealed guitar amplifier volume tone' },
      { type: 'impedance-selector', name: 'Rotary impedance selector', addLabel: 'Add rotary impedance selector', description: 'COM / 4Ω / 8Ω / 16Ω speaker selector', iconClass: 'layout-part-icon-impedance-selector', tags: 'rotary switch selector speaker output transformer impedance ohm common' },
      { type: 'carling-toggle-switch', construction: 'spst', name: 'Carling SPST', addLabel: 'Add Carling SPST switch', description: 'Rear view · 2 terminals', iconClass: 'layout-part-icon-carling-switch', tags: 'toggle switch on off single pole single throw rear' },
      { type: 'carling-toggle-switch', construction: 'dpst', name: 'Carling DPST', addLabel: 'Add Carling DPST switch', description: 'Rear view · 4 terminals', iconClass: 'layout-part-icon-carling-switch is-four-terminal', tags: 'toggle switch on off double pole single throw rear mains' },
      { type: 'carling-toggle-switch', construction: 'spdt', name: 'Carling SPDT', addLabel: 'Add Carling SPDT switch', description: 'Rear view · 3 terminals', iconClass: 'layout-part-icon-carling-switch is-three-terminal', tags: 'toggle switch single pole double throw common rear' },
      { type: 'carling-toggle-switch', construction: 'dpdt', name: 'Carling DPDT', addLabel: 'Add Carling DPDT switch', description: 'Rear view · 6 terminals', iconClass: 'layout-part-icon-carling-switch is-six-terminal', tags: 'toggle switch double pole double throw common rear' },
      { type: 'iec-power-inlet', name: 'IEC power inlet', addLabel: 'Add IEC power inlet', description: 'C14 rear view · line, neutral, earth', iconClass: 'layout-part-icon-iec-inlet', tags: 'power socket mains inlet c14 ac line neutral ground earth rear panel' },
      { type: 'panel-fuse-holder', name: 'Panel fuse holder', addLabel: 'Add panel fuse holder', description: 'Rear view · 2 solder terminals', iconClass: 'layout-part-icon-fuse-holder', tags: 'fuse holder panel mount mains protection rear solder terminal' },
      { type: 'input-jack', name: 'Input jack', addLabel: 'Add switched input jack', description: 'Four-lug switched contact', iconClass: 'layout-part-icon-jack', tags: 'socket connector phone guitar' },
      { type: 'parallel-output-jacks', name: 'Parallel output jacks', addLabel: 'Add parallel output jacks', description: 'Paired speaker outputs with linked contacts', iconClass: 'layout-part-icon-output-jacks', tags: 'speaker output socket parallel impedance' },
    ],
  },
  {
    id: 'resistors',
    label: 'Resistors',
    items: [
      { type: 'resistor', construction: 'carbon-composition', name: 'Carbon composition', addLabel: 'Add carbon composition resistor', description: 'Dark molded rectangular body', iconClass: 'layout-part-icon-resistor layout-part-icon-carbon-composition', tags: 'carbon comp vintage axial passive' },
      { type: 'resistor', construction: 'iskra-upm', name: 'ISKRA UPM', addLabel: 'Add ISKRA UPM resistor', description: 'Vintage mustard carbon-film resistor', iconClass: 'layout-part-icon-resistor layout-part-icon-iskra', tags: 'iskra upm vintage marshall carbon film mustard axial passive' },
      { type: 'resistor', construction: 'piher', name: 'Piher', addLabel: 'Add Piher resistor', description: 'Vintage burgundy carbon-film resistor', iconClass: 'layout-part-icon-resistor layout-part-icon-piher', tags: 'piher vintage marshall carbon film burgundy axial passive' },
      { type: 'resistor', construction: 'ceramic-composition', name: 'Ceramic composition', addLabel: 'Add ceramic composition resistor', description: 'High-energy molded resistor', iconClass: 'layout-part-icon-resistor layout-part-icon-ceramic-composition', tags: 'ceramic comp power axial passive' },
      { type: 'resistor', construction: 'metal-film', name: 'Metal film', addLabel: 'Add metal film resistor', description: 'Low-noise precision resistor', iconClass: 'layout-part-icon-resistor layout-part-icon-metal-film', tags: 'blue precision axial passive' },
      { type: 'resistor', construction: 'metal-oxide', name: 'Metal oxide', addLabel: 'Add metal oxide resistor', description: 'Heat-tolerant power resistor', iconClass: 'layout-part-icon-resistor layout-part-icon-metal-oxide', tags: 'oxide gray power axial passive' },
    ],
  },
  {
    id: 'capacitors',
    label: 'Capacitors',
    items: [
      { type: 'capacitor', construction: 'axial-film', name: 'Axial film', addLabel: 'Add axial film capacitor', description: 'General coupling capacitor', iconClass: 'layout-part-icon-capacitor', tags: 'coupling tone film passive' },
      { type: 'capacitor', construction: 'orange-drop', name: 'Orange Drop', addLabel: 'Add Orange Drop capacitor', description: 'Radial dipped film capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-orange-drop', tags: 'sprague radial coupling tone film' },
      { type: 'capacitor', construction: 'ceramic-disc', name: 'Ceramic disc', addLabel: 'Add ceramic disc capacitor', description: 'Round radial ceramic capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-ceramic-disc', tags: 'round radial bright tone snubber' },
      { type: 'capacitor', construction: 'silver-mica', name: 'Silver mica', addLabel: 'Add silver mica capacitor', description: 'Stable molded RF and tone capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-silver-mica', tags: 'mica molded treble tone bright cap radial' },
      { type: 'capacitor', construction: 'mullard-mustard', name: 'Mullard mustard', addLabel: 'Add Mullard mustard capacitor', description: 'Vintage axial coupling style', iconClass: 'layout-part-icon-capacitor layout-part-icon-mustard', tags: 'mustard vintage coupling film' },
      { type: 'capacitor', construction: 'sozo-coupling', name: 'SoZo coupling', addLabel: 'Add SoZo coupling capacitor', description: 'Modern axial amplifier coupling', iconClass: 'layout-part-icon-capacitor layout-part-icon-sozo', tags: 'sozo coupling film axial' },
      { type: 'capacitor', construction: 'board-axial-electrolytic', name: 'Board axial electrolytic', addLabel: 'Add board axial electrolytic capacitor', description: 'Compact low-voltage polarized capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-electrolytic', tags: 'sprague white cathode bypass board low voltage polarized axial' },
      { type: 'capacitor', construction: 'sprague-30d-electrolytic', name: 'Sprague 30D axial', addLabel: 'Add Sprague 30D axial electrolytic capacitor', description: 'Vintage compact aluminum electrolytic', iconClass: 'layout-part-icon-capacitor layout-part-icon-electrolytic', tags: 'sprague 30d silver aluminum vintage cathode bypass polarized axial' },
      { type: 'capacitor', construction: 'axial-electrolytic', name: 'Axial electrolytic', addLabel: 'Add axial electrolytic capacitor', description: 'Polarized tubular capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-electrolytic', tags: 'filter cathode bypass polarized' },
      { type: 'capacitor', construction: 'radial-electrolytic', name: 'Radial electrolytic', addLabel: 'Add radial electrolytic capacitor', description: 'Polarized upright capacitor', iconClass: 'layout-part-icon-capacitor layout-part-icon-radial-electrolytic', tags: 'filter cathode bypass polarized vertical' },
      { type: 'capacitor', construction: 'can-electrolytic', name: 'Can electrolytic', addLabel: 'Add can electrolytic capacitor', description: 'Chassis-mounted filter can', iconClass: 'layout-part-icon-capacitor layout-part-icon-can-capacitor', tags: 'filter can multi section power supply chassis' },
    ],
  },
  {
    id: 'diodes',
    label: 'Diodes',
    items: [
      { type: 'diode', construction: 'rectifier', name: 'Rectifier diode', addLabel: 'Add rectifier diode', description: 'High-voltage power-supply rectifier', iconClass: 'layout-part-icon-diode layout-part-icon-rectifier-diode', tags: '1n4007 silicon power supply rectification' },
      { type: 'diode', construction: 'phase-inverter', name: 'Phase inverter diode', addLabel: 'Add phase inverter diode', description: 'Small-signal protection and clamping diode', iconClass: 'layout-part-icon-diode layout-part-icon-signal-diode', tags: '1n4148 signal protection clamp phase inverter' },
    ],
  },
];

const ComponentPalette = ({ canvas, units, onAdd }) => {
  const [query, setQuery] = useState('');
  const [openFamilies, setOpenFamilies] = useState(() => new Set(COMPONENT_FAMILIES.map(family => family.id)));
  const normalizedQuery = query.trim().toLowerCase();
  const matchingFamilies = COMPONENT_FAMILIES.map(family => ({
    ...family,
    items: normalizedQuery
      ? family.items.filter(item => `${item.name} ${item.description} ${item.tags}`.toLowerCase().includes(normalizedQuery))
      : family.items,
  })).filter(family => family.items.length > 0);

  const toggleFamily = (familyId) => {
    setOpenFamilies(current => {
      const next = new Set(current);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };

  return (
    <aside className="layout-palette" aria-label="Component library">
      <div className="layout-panel-heading">
        <span>Component library</span>
        <small>Admin preview</small>
      </div>
      <div className="layout-palette-search">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search components"
          aria-label="Search components"
        />
        {query && <button type="button" aria-label="Clear component search" onClick={() => setQuery('')}>×</button>}
      </div>
      <div className="layout-family-list">
        {matchingFamilies.map(family => {
          const isOpen = normalizedQuery || openFamilies.has(family.id);
          return (
            <section className="layout-family" key={family.id}>
              <button
                type="button"
                className="layout-family-heading"
                aria-expanded={Boolean(isOpen)}
                onClick={() => toggleFamily(family.id)}
              >
                <span>{isOpen ? '▾' : '▸'} {family.label}</span>
                <span className="layout-family-count">{family.items.length}</span>
              </button>
              {isOpen && (
                <div className="layout-family-items">
                  {family.items.map(item => (
                    <button key={`${item.type}:${item.construction || 'default'}`} type="button" aria-label={item.addLabel} onClick={() => onAdd(item.type, item.construction)}>
                      <span className={`layout-part-icon ${item.iconClass}`} aria-hidden="true" />
                      <span><strong>{item.name}</strong><small>{item.description}</small></span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          );
        })}
        {matchingFamilies.length === 0 && (
          <div className="layout-palette-empty">No components match “{query.trim()}”.</div>
        )}
      </div>
      <div className="layout-scale-note">
        <strong>1:1 document</strong>
        <span>{formatMeasurement(canvas.width, units)} × {formatMeasurement(canvas.height, units)} {units}</span>
      </div>
    </aside>
  );
};

const ResistorReferenceSheet = ({ resistors, onClose }) => (
  <div className="layout-sheet-backdrop" role="presentation" onMouseDown={onClose}>
    <section
      className="layout-resistor-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resistor-sheet-title"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header>
        <div>
          <h2 id="resistor-sheet-title">Resistor reference</h2>
          <p>Exact values for color-band resistors in this layout.</p>
        </div>
        <button type="button" aria-label="Close resistor reference" onClick={onClose}>×</button>
      </header>
      {resistors.length > 0 ? (
        <table>
          <thead><tr><th>Reference</th><th>Value</th><th>Wattage</th><th>Color bands</th></tr></thead>
          <tbody>
            {resistors.map(resistor => (
              <tr key={resistor.id}>
                <td>{resistor.label}</td>
                <td>{resistor.value}</td>
                <td>{getResistorWattage(resistor)} W</td>
                <td>
                  <span className="layout-reference-bands" aria-label={`${resistor.label} color bands`}>
                    {getResistorBandColors(resistor.value).map((color, index) => (
                      <span key={`${color}-${index}`} style={{ backgroundColor: color }} />
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="layout-sheet-empty">This layout has no resistors.</p>
      )}
      <footer>
        <button type="button" onClick={() => window.print()}>Print reference</button>
      </footer>
    </section>
  </div>
);

const PrintCalibrationRule = ({ canvas }) => {
  const startX = 10;
  const baselineY = canvas.height - 10;
  return (
    <g className="layout-print-calibration" data-testid="layout-print-calibration">
      <rect x={startX - 3} y={baselineY - 11} width="106" height="15" rx="1" />
      <path d={`M ${startX} ${baselineY} H ${startX + 100}`} />
      {Array.from({ length: 11 }, (_, index) => (
        <path key={index} d={`M ${startX + index * 10} ${baselineY - (index % 5 === 0 ? 5 : 3)} V ${baselineY + 1}`} />
      ))}
      <text x={startX + 50} y={baselineY - 6} textAnchor="middle">100 mm print check</text>
    </g>
  );
};

const CanvasRulers = ({ canvas, units }) => {
  const minorStep = canvas.grid;
  const majorStep = units === 'in' ? MM_PER_INCH : 10;
  const horizontalTicks = Array.from({ length: Math.floor(canvas.width / minorStep) + 1 }, (_, index) => index * minorStep);
  const verticalTicks = Array.from({ length: Math.floor(canvas.height / minorStep) + 1 }, (_, index) => index * minorStep);
  const isMajorTick = value => Math.abs(value / majorStep - Math.round(value / majorStep)) < 0.001;
  const label = value => units === 'in' ? String(Math.round(value / MM_PER_INCH)) : String(Math.round(value / 10));

  return (
    <g className="layout-canvas-rulers" data-testid="layout-canvas-rulers" data-layout-export-exclude="true">
      <rect className="layout-ruler-surface" width={canvas.width} height="9" />
      <rect className="layout-ruler-surface" width="9" height={canvas.height} />
      <path className="layout-ruler-axis" d={`M 0 0 H ${canvas.width} M 0 0 V ${canvas.height}`} />
      {horizontalTicks.map(value => (
        <g key={`x-${value}`}>
          <path className="layout-ruler-tick" d={`M ${value} 0 V ${isMajorTick(value) ? 5 : 2.5}`} />
          {isMajorTick(value) && <text x={value + 1} y="8">{label(value)}</text>}
        </g>
      ))}
      {verticalTicks.map(value => (
        <g key={`y-${value}`}>
          <path className="layout-ruler-tick" d={`M 0 ${value} H ${isMajorTick(value) ? 5 : 2.5}`} />
          {value > 0 && isMajorTick(value) && <text x="8" y={value + 1} textAnchor="middle" transform={`rotate(-90 8 ${value + 1})`}>{label(value)}</text>}
        </g>
      ))}
      <text className="layout-ruler-unit" x="4.5" y="3.2" textAnchor="middle">{units}</text>
    </g>
  );
};

const LayoutStudio = forwardRef(({ document, onChange: setDocument }, ref) => {
  const svgRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const dragRef = useRef(null);
  const conductorDragRef = useRef(null);
  const leadDragRef = useRef(null);
  const idCounter = useRef(0);
  const clipboardRef = useRef(null);
  const documentRef = useRef(document);
  const emittedDocumentSnapshotsRef = useRef([]);
  const historyRef = useRef({ past: [], future: [] });
  const marqueeRef = useRef(null);
  const pointerHandlersRef = useRef({ move: null, finish: null });
  const [, setHistoryVersion] = useState(0);
  const [canvasScale, setCanvasScale] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [editorMode, setEditorMode] = useState('select');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedConductorId, setSelectedConductorId] = useState(null);
  const [selectedConductorIds, setSelectedConductorIds] = useState(() => new Set());
  const [pendingEndpoint, setPendingEndpoint] = useState(null);
  const [marquee, setMarquee] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [leadSnapActive, setLeadSnapActive] = useState(false);
  const [showResistorSheet, setShowResistorSheet] = useState(false);
  const selectedObject = document.objects.find(object => object.id === selectedId) || null;
  const selectedConductor = document.conductors.find(conductor => conductor.id === selectedConductorId) || null;
  const selectedConductors = document.conductors.filter(conductor => selectedConductorIds.has(conductor.id));
  const resistorDisplay = document.settings?.resistorDisplay === 'text' ? 'text' : 'bands';
  const displayUnits = document.settings?.displayUnits === 'in' ? 'in' : 'cm';
  const majorGridSpacing = displayUnits === 'in' ? MM_PER_INCH : 10;
  const printCalibration = document.settings?.printCalibration === true;
  const showRulers = document.settings?.showRulers === true;
  const resistors = document.objects
    .filter(object => object.type === 'resistor')
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }));
  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;

  useImperativeHandle(ref, () => ({
    exportFile: (format, title) => exportLayoutFile(svgRef.current, document.canvas, format, title),
  }), [document.canvas]);

  useEffect(() => {
    documentRef.current = document;
    const snapshot = JSON.stringify(document);
    const emittedIndex = emittedDocumentSnapshotsRef.current.indexOf(snapshot);
    if (emittedIndex >= 0) {
      emittedDocumentSnapshotsRef.current.splice(0, emittedIndex + 1);
      return;
    }
    emittedDocumentSnapshotsRef.current = [];
    historyRef.current = { past: [], future: [] };
    dragRef.current = null;
    conductorDragRef.current = null;
    leadDragRef.current = null;
    setHistoryVersion(version => version + 1);
  }, [document]);

  useEffect(() => {
    if (selectedId && !document.objects.some(object => object.id === selectedId)) setSelectedId(null);
    setSelectedIds(current => {
      const next = new Set([...current].filter(id => document.objects.some(object => object.id === id)));
      return next.size === current.size ? current : next;
    });
    setSelectedConductorIds(current => {
      const next = new Set([...current].filter(id => document.conductors.some(conductor => conductor.id === id)));
      return next.size === current.size ? current : next;
    });
    if (selectedConductorId && !document.conductors.some(conductor => conductor.id === selectedConductorId)) setSelectedConductorId(null);
    if (pendingEndpoint) {
      const object = document.objects.find(item => item.id === pendingEndpoint.objectId);
      if (!object || !getObjectTerminals(object).some(terminal => terminal.id === pendingEndpoint.terminalId)) setPendingEndpoint(null);
    }
  }, [document, pendingEndpoint, selectedConductorId, selectedId]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const closeMenu = () => setContextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    window.addEventListener('pointerdown', closeMenu);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('pointerdown', closeMenu);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [contextMenu]);

  const emitDocument = (nextDocument) => {
    documentRef.current = nextDocument;
    emittedDocumentSnapshotsRef.current.push(JSON.stringify(nextDocument));
    setDocument(nextDocument);
  };

  const applyDocumentChange = (updater, recordHistory = true) => {
    const current = documentRef.current;
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (!next || next === current) return;
    if (next.canvas.width !== current.canvas.width || next.canvas.height !== current.canvas.height) {
      let scale = canvasScale;
      if (!scale && svgRef.current) {
        const bounds = svgRef.current.getBoundingClientRect();
        scale = Math.min(bounds.width / current.canvas.width, bounds.height / current.canvas.height);
        setCanvasScale(scale);
      }
    }
    if (recordHistory) {
      historyRef.current.past.push(current);
      historyRef.current.future = [];
      setHistoryVersion(version => version + 1);
    }
    emitDocument(next);
  };

  const onChange = (updater) => applyDocumentChange(updater, true);

  const undo = () => {
    if (historyRef.current.past.length === 0) return;
    const current = documentRef.current;
    const previous = historyRef.current.past.pop();
    historyRef.current.future.push(current);
    emitDocument(previous);
    setHistoryVersion(version => version + 1);
  };

  const redo = () => {
    if (historyRef.current.future.length === 0) return;
    const current = documentRef.current;
    const next = historyRef.current.future.pop();
    historyRef.current.past.push(current);
    emitDocument(next);
    setHistoryVersion(version => version + 1);
  };

  const setResistorDisplay = (mode) => {
    onChange(current => ({
      ...current,
      settings: { ...current.settings, resistorDisplay: mode },
    }));
    if (mode === 'text') setShowResistorSheet(false);
  };

  const setUnits = (units) => {
    onChange(current => ({
      ...current,
      settings: { ...current.settings, displayUnits: units },
    }));
  };

  const setPrintCalibration = (enabled) => {
    onChange(current => ({
      ...current,
      settings: { ...current.settings, printCalibration: enabled },
    }));
  };

  const setShowRulers = (enabled) => {
    onChange(current => ({
      ...current,
      settings: { ...current.settings, showRulers: enabled },
    }));
  };

  const setGridSpacing = (grid) => {
    onChange(current => ({
      ...current,
      canvas: { ...current.canvas, grid },
    }));
  };

  const setZoom = (nextZoom) => {
    const canvas = svgRef.current;
    const canvasWrap = canvasWrapRef.current;
    if (!canvas || !canvasWrap) return;
    const boundedZoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
    if (boundedZoom === zoomLevel) return;
    const bounds = canvas.getBoundingClientRect();
    const currentScale = canvasScale || Math.min(
      bounds.width / document.canvas.width,
      bounds.height / document.canvas.height
    );
    if (!Number.isFinite(currentScale) || currentScale <= 0) return;
    const nextScale = currentScale / zoomLevel * boundedZoom;
    const scaleRatio = nextScale / currentScale;
    const centerX = canvasWrap.scrollLeft + canvasWrap.clientWidth / 2;
    const centerY = canvasWrap.scrollTop + canvasWrap.clientHeight / 2;
    setCanvasScale(nextScale);
    setZoomLevel(boundedZoom);
    requestAnimationFrame(() => {
      canvasWrap.scrollLeft = centerX * scaleRatio - canvasWrap.clientWidth / 2;
      canvasWrap.scrollTop = centerY * scaleRatio - canvasWrap.clientHeight / 2;
    });
  };

  const addObject = (type, construction) => {
    idCounter.current += 1;
    const object = createLayoutObject(type, `${type}-${Date.now()}-${idCounter.current}`, { construction });
    const reference = getNextLayoutReference(documentRef.current, type);
    if (reference) object.label = reference;
    if (type === 'turret') {
      const turretCount = documentRef.current.objects.filter(item => item.type === 'turret').length;
      const spacing = Math.max(documentRef.current.canvas.grid, 10);
      object.x += (turretCount % 12) * spacing;
      object.y += Math.floor(turretCount / 12) * spacing;
    }
    onChange(current => expandLayoutCanvasToFit(addLayoutObject(current, object)));
    setSelectedId(object.id);
    setSelectedIds(new Set([object.id]));
    setSelectedConductorId(null);
    setSelectedConductorIds(new Set());
  };

  const updateSelected = (changes) => {
    if (!selectedId) return;
    onChange(current => expandLayoutCanvasToFit(updateLayoutObject(current, selectedId, changes)));
  };

  const updatePassiveConstruction = (construction) => {
    if (!selectedObject) return;
    const definition = selectedObject.type === 'resistor'
      ? RESISTOR_LIBRARY[construction]
      : CAPACITOR_LIBRARY[construction];
    if (!definition) return;
    updateSelected({
      construction,
      packageStyle: definition.packageStyle,
      value: definition.value,
      width: definition.width,
      height: definition.height,
      voltageRating: definition.voltageRating || null,
      ...(definition.sleeveColor ? { sleeveColor: definition.sleeveColor } : {}),
      ...(construction === 'can-electrolytic' ? {
        sections: selectedObject.sections || DEFAULT_CAN_CAPACITOR_SECTIONS.map(section => ({ ...section })),
        value: selectedObject.sections
          ? selectedObject.sections.map(section => section.capacitance).join(' / ')
          : definition.value,
      } : {}),
      ...(selectedObject.type === 'resistor' ? {
        wattage: getResistorWattageOptions(construction).includes(Number(selectedObject.wattage))
          ? Number(selectedObject.wattage)
          : definition.wattage,
      } : {}),
    });
  };

  const updateDiodeConstruction = (construction) => {
    const definition = DIODE_LIBRARY[construction];
    if (!definition) return;
    updateSelected({
      construction,
      value: definition.value,
      width: definition.width,
      height: definition.height,
    });
  };

  const updateCanCapacitorSection = (sectionId, changes) => {
    const sections = (selectedObject.sections || DEFAULT_CAN_CAPACITOR_SECTIONS).map(section => (
      section.id === sectionId ? { ...section, ...changes } : { ...section }
    ));
    onChange(current => updateCanCapacitorSections(current, selectedObject.id, sections));
  };

  const addCanCapacitorSection = () => {
    const sections = selectedObject.sections || DEFAULT_CAN_CAPACITOR_SECTIONS;
    if (sections.length >= 4) return;
    const usedIds = new Set(sections.map(section => section.id));
    const suffix = ['a', 'b', 'c', 'd'].find(candidate => !usedIds.has(`section-${candidate}`));
    const symbol = { a: 'semicircle', b: 'triangle', c: 'square', d: 'blank' }[suffix];
    const nextSections = [...sections, {
      id: `section-${suffix}`,
      label: `Section ${suffix.toUpperCase()}`,
      capacitance: '32µF',
      symbol,
    }];
    onChange(current => updateCanCapacitorSections(current, selectedObject.id, nextSections));
  };

  const removeCanCapacitorSection = (sectionId) => {
    const sections = selectedObject.sections || DEFAULT_CAN_CAPACITOR_SECTIONS;
    if (sections.length <= 1) return;
    onChange(current => updateCanCapacitorSections(
      current,
      selectedObject.id,
      sections.filter(section => section.id !== sectionId)
    ));
  };

  const updateSelectedTransformerTaps = (updater) => {
    if (!selectedObject || selectedObject.type !== 'power-transformer') return;
    onChange(current => {
      const transformer = current.objects.find(object => object.id === selectedObject.id);
      const taps = getPowerTransformerTaps(transformer);
      return updatePowerTransformerTaps(current, selectedObject.id, updater(taps));
    });
  };

  const updateTransformerTap = (side, tapId, changes) => {
    updateSelectedTransformerTaps(taps => ({
      ...taps,
      [side]: taps[side].map(tap => tap.id === tapId ? { ...tap, ...changes } : tap),
    }));
  };

  const addTransformerTap = (side) => {
    idCounter.current += 1;
    const tap = {
      id: `tap-${Date.now()}-${idCounter.current}`,
      label: 'Winding',
      specification: '0',
      color: BRIGHT_WIRE_COLORS[3],
    };
    updateSelectedTransformerTaps(taps => ({ ...taps, [side]: [...taps[side], tap] }));
  };

  const removeTransformerTap = (side, tapId) => {
    updateSelectedTransformerTaps(taps => ({
      ...taps,
      [side]: taps[side].filter(tap => tap.id !== tapId),
    }));
  };

  const duplicateSelected = () => {
    if (!selectedObject) return;
    const sourceIds = documentRef.current.objects
      .filter(object => selectedIds.has(object.id))
      .map(object => object.id);
    const duplicateIds = sourceIds.map(sourceId => {
      const source = documentRef.current.objects.find(object => object.id === sourceId);
      idCounter.current += 1;
      return `${source.type}-${Date.now()}-${idCounter.current}`;
    });
    const duplicateIdBySource = new Map(sourceIds.map((sourceId, index) => [sourceId, duplicateIds[index]]));
    const copiedGroups = documentRef.current.groups.filter(group => group.objectIds.every(id => selectedIds.has(id)));
    const duplicateGroupIds = copiedGroups.map(() => {
      idCounter.current += 1;
      return `group-${Date.now()}-${idCounter.current}`;
    });
    onChange(current => {
      const withCopies = sourceIds.reduce(
        (next, sourceId, index) => duplicateLayoutObject(next, sourceId, duplicateIds[index]),
        current
      );
      return copiedGroups.reduce((next, group, index) => createLayoutGroup(
        next,
        duplicateGroupIds[index],
        group.objectIds.map(id => duplicateIdBySource.get(id))
      ), withCopies);
    });
    setSelectedId(duplicateIds[duplicateIds.length - 1]);
    setSelectedIds(new Set(duplicateIds));
    setSelectedConductorId(null);
    setSelectedConductorIds(new Set());
  };

  const copySelected = () => {
    const current = documentRef.current;
    const objectIds = new Set(selectedIds);
    const conductorIds = objectIds.size > 0
      ? new Set(current.conductors
        .filter(conductor => objectIds.has(conductor.from.objectId) && objectIds.has(conductor.to.objectId))
        .map(conductor => conductor.id))
      : new Set(selectedConductorIds);
    if (objectIds.size === 0 && conductorIds.size === 0) return false;
    const clone = value => JSON.parse(JSON.stringify(value));
    clipboardRef.current = {
      objects: clone(current.objects.filter(object => objectIds.has(object.id))),
      conductors: clone(current.conductors.filter(conductor => conductorIds.has(conductor.id))),
      groups: clone(current.groups.filter(group => group.objectIds.every(id => objectIds.has(id)))),
      pasteCount: 0,
    };
    return true;
  };

  const cutSelected = () => {
    if (!copySelected()) return;
    deleteSelected();
  };

  const pasteClipboard = () => {
    const clipboard = clipboardRef.current;
    if (!clipboard) return;
    clipboard.pasteCount += 1;
    const timestamp = Date.now();
    const objectIdMap = new Map(clipboard.objects.map(object => {
      idCounter.current += 1;
      return [object.id, `${object.type}-${timestamp}-${idCounter.current}`];
    }));
    const conductorIdMap = new Map(clipboard.conductors.map(conductor => {
      idCounter.current += 1;
      return [conductor.id, `wire-${timestamp}-${idCounter.current}`];
    }));
    const braidIdMap = new Map();
    const groupIdMap = new Map(clipboard.groups.map(group => {
      idCounter.current += 1;
      return [group.id, `group-${timestamp}-${idCounter.current}`];
    }));
    const offset = documentRef.current.canvas.grid * clipboard.pasteCount;
    const pastedObjects = clipboard.objects.map(object => ({
      ...JSON.parse(JSON.stringify(object)),
      id: objectIdMap.get(object.id),
      x: object.x + offset,
      y: object.y + offset,
    }));
    const pastedConductors = clipboard.conductors.map((conductor, index) => {
      const copy = JSON.parse(JSON.stringify(conductor));
      copy.id = conductorIdMap.get(conductor.id) || `wire-${timestamp}-${index}`;
      copy.from.objectId = objectIdMap.get(conductor.from.objectId) || conductor.from.objectId;
      copy.to.objectId = objectIdMap.get(conductor.to.objectId) || conductor.to.objectId;
      if (copy.braid?.id) {
        if (!braidIdMap.has(copy.braid.id)) {
          idCounter.current += 1;
          braidIdMap.set(copy.braid.id, `braid-${timestamp}-${idCounter.current}`);
        }
        copy.braid.id = braidIdMap.get(copy.braid.id);
      }
      return copy;
    });
    const pastedGroups = clipboard.groups.map((group, index) => ({
      ...JSON.parse(JSON.stringify(group)),
      id: groupIdMap.get(group.id) || `group-${timestamp}-${index}`,
      objectIds: group.objectIds.map(id => objectIdMap.get(id)),
    }));
    onChange(current => expandLayoutCanvasToFit({
      ...current,
      objects: [...current.objects, ...pastedObjects],
      conductors: [...current.conductors, ...pastedConductors],
      groups: [...current.groups, ...pastedGroups],
    }));
    const pastedObjectIds = pastedObjects.map(object => object.id);
    const pastedConductorIds = pastedConductors.map(conductor => conductor.id);
    setSelectedId(pastedObjectIds.at(-1) || null);
    setSelectedIds(new Set(pastedObjectIds));
    setSelectedConductorId(pastedObjectIds.length === 0 ? pastedConductorIds.at(-1) || null : null);
    setSelectedConductorIds(new Set(pastedObjectIds.length === 0 ? pastedConductorIds : []));
  };

  const deleteSelected = () => {
    if (selectedConductorIds.size > 0) {
      onChange(current => [...selectedConductorIds].reduce(removeConductor, current));
      setSelectedConductorId(null);
      setSelectedConductorIds(new Set());
    } else if (selectedIds.size > 0) {
      onChange(current => [...selectedIds].reduce(removeLayoutObject, current));
      setSelectedId(null);
      setSelectedIds(new Set());
    }
  };

  const groupSelected = () => {
    if (selectedIds.size < 2) return;
    idCounter.current += 1;
    onChange(current => createLayoutGroup(current, `group-${Date.now()}-${idCounter.current}`, [...selectedIds]));
  };

  const ungroupSelected = () => {
    const groupIds = documentRef.current.groups
      .filter(group => group.objectIds.some(id => selectedIds.has(id)))
      .map(group => group.id);
    if (groupIds.length === 0) return;
    onChange(current => groupIds.reduce(removeLayoutGroup, current));
  };

  const alignSelected = (axis, alignment) => {
    const selected = documentRef.current.objects.filter(object => selectedIds.has(object.id));
    if (selected.length < 2) return;
    const grid = documentRef.current.canvas.grid;
    const isHorizontal = axis === 'x';
    const start = Math.min(...selected.map(object => object[axis]));
    const end = Math.max(...selected.map(object => object[axis] + (isHorizontal ? object.width : object.height)));
    const center = (start + end) / 2;
    onChange(current => ({
      ...current,
      objects: current.objects.map(object => {
        if (!selectedIds.has(object.id)) return object;
        const size = isHorizontal ? object.width : object.height;
        const rawPosition = alignment === 'start' ? start : alignment === 'center' ? center - size / 2 : end - size;
        const limit = (isHorizontal ? current.canvas.width : current.canvas.height) - size;
        return { ...object, [axis]: clamp(Math.round(rawPosition / grid) * grid, 0, limit) };
      }),
    }));
  };

  const distributeSelected = (axis) => {
    const isHorizontal = axis === 'x';
    const sizeKey = isHorizontal ? 'width' : 'height';
    const selected = documentRef.current.objects
      .filter(object => selectedIds.has(object.id))
      .sort((left, right) => left[axis] - right[axis]);
    if (selected.length < 3) return;
    const grid = documentRef.current.canvas.grid;
    const first = selected[0][axis];
    const lastEdge = selected[selected.length - 1][axis] + selected[selected.length - 1][sizeKey];
    const totalSize = selected.reduce((sum, object) => sum + object[sizeKey], 0);
    const gap = (lastEdge - first - totalSize) / (selected.length - 1);
    const positions = new Map([[selected[0].id, first]]);
    let cursor = first + selected[0][sizeKey] + gap;
    selected.slice(1, -1).forEach(object => {
      positions.set(object.id, Math.round(cursor / grid) * grid);
      cursor += object[sizeKey] + gap;
    });
    onChange(current => ({
      ...current,
      objects: current.objects.map(object => positions.has(object.id)
        ? { ...object, [axis]: positions.get(object.id) }
        : object),
    }));
  };

  const chooseMode = (mode) => {
    setEditorMode(mode);
    setPendingEndpoint(null);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName?.toLowerCase();
      const isEditing = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || event.target?.isContentEditable;
      if (event.key === 'Escape') {
        setPendingEndpoint(null);
        setEditorMode('select');
        setShowResistorSheet(false);
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && (selectedId || selectedConductorIds.size > 0)) {
        if (isEditing) return;
        event.preventDefault();
        deleteSelected();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd' && selectedObject && !isEditing) {
        event.preventDefault();
        duplicateSelected();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c' && !isEditing) {
        if (selectedIds.size > 0 || selectedConductorIds.size > 0) {
          event.preventDefault();
          copySelected();
        }
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'x' && !isEditing) {
        if (selectedIds.size > 0 || selectedConductorIds.size > 0) {
          event.preventDefault();
          cutSelected();
        }
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v' && clipboardRef.current && !isEditing) {
        event.preventDefault();
        pasteClipboard();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'g' && selectedObject && !isEditing) {
        event.preventDefault();
        if (event.shiftKey) ungroupSelected();
        else groupSelected();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !isEditing) {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y' && !isEditing) {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const toCanvasPoint = (event) => {
    const bounds = svgRef.current.getBoundingClientRect();
    return {
      x: (event.clientX - bounds.left) * document.canvas.width / bounds.width,
      y: (event.clientY - bounds.top) * document.canvas.height / bounds.height,
    };
  };

  const toDragPoint = (event) => {
    const { canvasAtStart, boundsAtStart, scrollAtStart } = dragRef.current;
    const canvasWrap = canvasWrapRef.current;
    const scrollX = canvasWrap ? canvasWrap.scrollLeft - scrollAtStart.left : 0;
    const scrollY = canvasWrap ? canvasWrap.scrollTop - scrollAtStart.top : 0;
    return {
      x: (event.clientX - boundsAtStart.left + scrollX) * canvasAtStart.width / boundsAtStart.width,
      y: (event.clientY - boundsAtStart.top + scrollY) * canvasAtStart.height / boundsAtStart.height,
    };
  };

  const scrollDuringDrag = (event) => {
    const canvasWrap = canvasWrapRef.current;
    if (!canvasWrap) return;
    const bounds = canvasWrap.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const edgeSize = 36;
    const maxStep = 24;
    const horizontalStep = event.clientX > bounds.right - edgeSize
      ? Math.min(maxStep, event.clientX - (bounds.right - edgeSize))
      : event.clientX < bounds.left + edgeSize
        ? -Math.min(maxStep, bounds.left + edgeSize - event.clientX)
        : 0;
    const verticalStep = event.clientY > bounds.bottom - edgeSize
      ? Math.min(maxStep, event.clientY - (bounds.bottom - edgeSize))
      : event.clientY < bounds.top + edgeSize
        ? -Math.min(maxStep, bounds.top + edgeSize - event.clientY)
        : 0;
    if (canvasWrap.scrollWidth > canvasWrap.clientWidth) canvasWrap.scrollLeft += horizontalStep;
    if (canvasWrap.scrollHeight > canvasWrap.clientHeight) canvasWrap.scrollTop += verticalStep;
  };

  const beginDrag = (event, object) => {
    event.stopPropagation();
    if (event.button != null && event.button !== 0) return;
    if (editorMode !== 'select') return;
    event.preventDefault();
    const groupIds = new Set(getLayoutGroupObjectIds(documentRef.current, object.id));
    const isGroupSelected = [...groupIds].every(id => selectedIds.has(id));
    if (event.shiftKey && isGroupSelected) {
      const next = new Set(selectedIds);
      groupIds.forEach(id => next.delete(id));
      setSelectedIds(next);
      setSelectedId(next.size ? [...next][next.size - 1] : null);
      return;
    }
    const dragIds = event.shiftKey
      ? new Set([...selectedIds, ...groupIds])
      : selectedIds.has(object.id) ? new Set(selectedIds) : groupIds;
    svgRef.current?.setPointerCapture?.(event.pointerId);
    const boundsAtStart = svgRef.current.getBoundingClientRect();
    const point = toCanvasPoint(event);
    dragRef.current = {
      id: object.id,
      ids: dragIds,
      startPoint: point,
      startPositions: new Map(documentRef.current.objects
        .filter(item => dragIds.has(item.id))
        .map(item => [item.id, { x: item.x, y: item.y }])),
      startDocument: documentRef.current,
      canvasAtStart: { ...documentRef.current.canvas },
      boundsAtStart,
      scrollAtStart: {
        left: canvasWrapRef.current?.scrollLeft || 0,
        top: canvasWrapRef.current?.scrollTop || 0,
      },
      turretBoardTargets: new Map(),
      pointerId: event.pointerId,
      moved: false,
    };
    setSelectedId(object.id);
    setSelectedIds(dragIds);
    setSelectedConductorId(null);
    setSelectedConductorIds(new Set());
  };

  const beginMarquee = (event) => {
    const isBackground = event.target === event.currentTarget || event.target.dataset.layoutBackground === 'true';
    if (editorMode !== 'select' || !isBackground) return;
    event.preventDefault();
    const point = toCanvasPoint(event);
    marqueeRef.current = { start: point, baseIds: event.shiftKey ? new Set(selectedIds) : new Set() };
    setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
    if (!event.shiftKey) {
      setSelectedId(null);
      setSelectedIds(new Set());
      setSelectedConductorId(null);
      setSelectedConductorIds(new Set());
    }
  };

  const openObjectContextMenu = (event, object) => {
    event.preventDefault();
    event.stopPropagation();
    setEditorMode('select');
    setPendingEndpoint(null);
    const groupIds = new Set(getLayoutGroupObjectIds(documentRef.current, object.id));
    if (!selectedIds.has(object.id)) {
      setSelectedId(object.id);
      setSelectedIds(groupIds);
      setSelectedConductorId(null);
      setSelectedConductorIds(new Set());
    }
    const menuWidth = 248;
    const menuHeight = 310;
    setContextMenu({
      objectId: object.id,
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 8)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 8)),
    });
  };

  const openCanvasObjectContextMenu = (event) => {
    const objectElement = event.target.closest?.('[data-layout-object-id]');
    if (!objectElement) return;
    const object = documentRef.current.objects.find(item => item.id === objectElement.dataset.layoutObjectId);
    if (object) openObjectContextMenu(event, object);
  };

  const openSelectedObjectMenu = (event) => {
    if (!selectedObject) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    openObjectContextMenu({
      preventDefault: () => {},
      stopPropagation: () => {},
      clientX: bounds.right - 248,
      clientY: bounds.bottom + 4,
    }, selectedObject);
  };

  const rotateContextSelection = (rotation) => {
    const ids = new Set(selectedIds);
    onChange(current => ({
      ...current,
      objects: current.objects.map(object => ids.has(object.id) ? { ...object, rotation } : object),
    }));
  };

  const chooseTerminal = (event, object, terminal) => {
    event.stopPropagation();
    if (editorMode !== 'wire') {
      setSelectedId(object.id);
      setSelectedIds(new Set([object.id]));
      setSelectedConductorId(null);
      setSelectedConductorIds(new Set());
      return;
    }

    const endpoint = { objectId: object.id, terminalId: terminal.id };
    if (!pendingEndpoint) {
      setPendingEndpoint(endpoint);
      return;
    }
    if (pendingEndpoint.objectId === endpoint.objectId && pendingEndpoint.terminalId === endpoint.terminalId) return;

    idCounter.current += 1;
    const conductor = createConductor(`wire-${Date.now()}-${idCounter.current}`, pendingEndpoint, endpoint);
    onChange(current => addConductor(current, conductor));
    setPendingEndpoint(null);
    setSelectedId(null);
    setSelectedIds(new Set());
    setSelectedConductorId(conductor.id);
    setSelectedConductorIds(new Set([conductor.id]));
  };

  const beginConductorDrag = (event, conductorId) => {
    event.stopPropagation();
    if (editorMode !== 'select') return;
    event.preventDefault();
    const conductor = documentRef.current.conductors.find(item => item.id === conductorId);
    const from = conductor && resolveTerminalPosition(documentRef.current, conductor.from);
    const to = conductor && resolveTerminalPosition(documentRef.current, conductor.to);
    if (!conductor || !from || !to) return;
    if (event.shiftKey) {
      const next = new Set(selectedConductorIds);
      if (next.has(conductorId)) next.delete(conductorId);
      else next.add(conductorId);
      setSelectedConductorIds(next);
      setSelectedConductorId(next.has(conductorId) ? conductorId : ([...next].pop() || null));
      setSelectedId(null);
      setSelectedIds(new Set());
      return;
    }
    const point = toCanvasPoint(event);
    const { control1, control2 } = getConductorCurveControls(conductor, from, to);
    svgRef.current?.setPointerCapture?.(event.pointerId);
    conductorDragRef.current = {
      conductorId,
      parameter: findClosestCurveParameter(point, from, control1, control2, to),
      startPoint: point,
      startConductor: conductor,
      startDocument: documentRef.current,
      pointerId: event.pointerId,
      moved: false,
    };
    setSelectedId(null);
    setSelectedIds(new Set());
    setSelectedConductorId(conductorId);
    setSelectedConductorIds(new Set([conductorId]));
  };

  const beginLeadDrag = (event, object, lead, pointName) => {
    event.stopPropagation();
    if (editorMode !== 'select') return;
    event.preventDefault();
    svgRef.current?.setPointerCapture?.(event.pointerId);
    leadDragRef.current = {
      objectId: object.id,
      terminalId: lead.terminalId,
      pointName,
      startDocument: documentRef.current,
      pointerId: event.pointerId,
      moved: false,
      snapEligible: pointName === 'terminal',
    };
    if (pointName === 'terminal') setLeadSnapActive(true);
    setSelectedId(object.id);
    setSelectedIds(new Set([object.id]));
    setSelectedConductorId(null);
    setSelectedConductorIds(new Set());
  };

  const toggleSelectedBraid = () => {
    if (selectedConductors.length !== 2) return;
    const isBraidedPair = selectedConductors[0].braid?.id
      && selectedConductors[0].braid.id === selectedConductors[1].braid?.id;
    const conductorIds = selectedConductors.map(conductor => conductor.id);
    onChange(current => isBraidedPair
      ? unbraidConductors(current, conductorIds)
      : braidConductors(current, conductorIds, `braid-${Date.now()}`));
  };

  const updateSelectedConductor = (changes) => {
    if (!selectedConductorId) return;
    onChange(current => updateConductor(current, selectedConductorId, changes));
  };

  const reorderSelected = (direction) => {
    const kind = selectedConductorId ? 'conductor' : 'object';
    const id = selectedConductorId || selectedId;
    if (!id) return;
    onChange(current => reorderLayoutItem(current, kind, id, direction));
  };

  const dragObject = (event) => {
    if (leadDragRef.current) {
      const drag = leadDragRef.current;
      const object = documentRef.current.objects.find(item => item.id === drag.objectId);
      if (!object) return;
      const point = toCanvasPoint(event);
      const localPoint = toObjectLocalPoint(object, point);
      const definitions = getAxialLeadDefinitions(object);
      const lead = definitions.find(item => item.terminalId === drag.terminalId);
      if (!lead) return;
      const existingLead = object.leads?.[drag.terminalId] || {};
      const leads = {
        ...(object.leads || {}),
        [drag.terminalId]: {
          ...(drag.pointName === 'hinge' && existingLead.connection ? { connection: existingLead.connection } : {}),
          hinge: { ...lead.hinge },
          terminal: { ...lead.terminal },
          [drag.pointName]: localPoint,
        },
      };
      drag.moved = true;
      applyDocumentChange(updateLayoutObject(documentRef.current, object.id, { leads }), false);
      return;
    }
    if (conductorDragRef.current) {
      const drag = conductorDragRef.current;
      const point = toCanvasPoint(event);
      const delta = { x: point.x - drag.startPoint.x, y: point.y - drag.startPoint.y };
      if (delta.x === 0 && delta.y === 0) return;
      drag.moved = true;
      applyDocumentChange(updateConductor(
        documentRef.current,
        drag.conductorId,
        { curve: reshapeConductorCurve(drag.startConductor, drag.parameter, delta) }
      ), false);
      return;
    }
    if (marqueeRef.current) {
      const point = toCanvasPoint(event);
      const { start, baseIds } = marqueeRef.current;
      const bounds = {
        x: Math.min(start.x, point.x),
        y: Math.min(start.y, point.y),
        width: Math.abs(point.x - start.x),
        height: Math.abs(point.y - start.y),
      };
      const directHits = documentRef.current.objects.filter(object => (
        object.x < bounds.x + bounds.width
        && object.x + object.width > bounds.x
        && object.y < bounds.y + bounds.height
        && object.y + object.height > bounds.y
      )).map(object => object.id);
      const hits = directHits.flatMap(id => getLayoutGroupObjectIds(documentRef.current, id));
      const next = new Set([...baseIds, ...hits]);
      setMarquee(bounds);
      setSelectedIds(next);
      setSelectedId(hits[hits.length - 1] || (next.size ? [...next][next.size - 1] : null));
      setSelectedConductorId(null);
      setSelectedConductorIds(new Set());
      return;
    }
    if (!dragRef.current) return;
    scrollDuringDrag(event);
    const point = toDragPoint(event);
    const current = documentRef.current;
    const objects = current.objects.filter(item => dragRef.current.ids.has(item.id));
    if (objects.length === 0) return;
    const grid = current.canvas.grid;
    const requestedX = Math.round((point.x - dragRef.current.startPoint.x) / grid) * grid;
    const requestedY = Math.round((point.y - dragRef.current.startPoint.y) / grid) * grid;
    const deltaX = Math.max(requestedX, Math.max(...objects.map(object => -dragRef.current.startPositions.get(object.id).x)));
    const deltaY = Math.max(requestedY, Math.max(...objects.map(object => -dragRef.current.startPositions.get(object.id).y)));
    if (deltaX === 0 && deltaY === 0) return;
    dragRef.current.moved = true;
    const nextDocument = expandLayoutCanvasToFit({
      ...current,
      canvas: { ...dragRef.current.startDocument.canvas },
      objects: current.objects.map(object => {
        const start = dragRef.current.startPositions.get(object.id);
        return start ? { ...object, x: start.x + deltaX, y: start.y + deltaY } : object;
      }),
    });
    dragRef.current.turretBoardTargets = new Map(nextDocument.objects
      .filter(object => dragRef.current.ids.has(object.id) && object.type === 'turret')
      .map(turret => {
        const center = { x: turret.x + turret.width / 2, y: turret.y + turret.height / 2 };
        const board = [...nextDocument.objects].reverse().find(object => {
          if (dragRef.current.ids.has(object.id) || !['turret-board', 'blank-turret-board'].includes(object.type)) return false;
          const local = toObjectLocalPoint(object, center);
          return local.x >= 0 && local.x <= object.width && local.y >= 0 && local.y <= object.height;
        });
        return [turret.id, board?.id];
      })
      .filter(([, boardId]) => boardId));
    applyDocumentChange(nextDocument, false);
  };

  const finishDrag = () => {
    const leadDrag = leadDragRef.current;
    if (leadDrag) {
      if (svgRef.current?.hasPointerCapture?.(leadDrag.pointerId)) {
        svgRef.current.releasePointerCapture(leadDrag.pointerId);
      }
      leadDragRef.current = null;
      setLeadSnapActive(false);
      if (!leadDrag.moved) return;
      if (leadDrag.snapEligible) {
        const current = documentRef.current;
        const object = current.objects.find(item => item.id === leadDrag.objectId);
        const sourceEndpoint = { objectId: leadDrag.objectId, terminalId: leadDrag.terminalId };
        const sourcePosition = resolveTerminalPosition(current, sourceEndpoint);
        const target = current.objects
          .filter(item => item.id !== leadDrag.objectId)
          .flatMap(item => getObjectTerminals(item).map(terminal => {
            const endpoint = { objectId: item.id, terminalId: terminal.id };
            const position = resolveTerminalPosition(current, endpoint);
            return {
              endpoint,
              position,
              distance: Math.hypot(position.x - sourcePosition.x, position.y - sourcePosition.y),
            };
          }))
          .filter(candidate => candidate.distance <= 7.5)
          .sort((left, right) => left.distance - right.distance)[0];
        if (object && target) {
          const lead = getAxialLeadDefinitions(object).find(item => item.terminalId === leadDrag.terminalId);
          const leads = {
            ...(object.leads || {}),
            [leadDrag.terminalId]: {
              hinge: { ...lead.hinge },
              terminal: toObjectLocalPoint(object, target.position),
              connection: target.endpoint,
            },
          };
          let nextDocument = updateLayoutObject(current, object.id, { leads });
          const targetGroupIds = getLayoutGroupObjectIds(nextDocument, target.endpoint.objectId);
          const board = nextDocument.objects.find(item => (
            targetGroupIds.includes(item.id)
            && ['turret-board', 'blank-turret-board'].includes(item.type)
          ));
          if (board) {
            idCounter.current += 1;
            nextDocument = createLayoutGroup(
              nextDocument,
              `group-${Date.now()}-${idCounter.current}`,
              [...targetGroupIds, object.id]
            );
          }
          applyDocumentChange(nextDocument, false);
        }
      }
      historyRef.current.past.push(leadDrag.startDocument);
      historyRef.current.future = [];
      setHistoryVersion(version => version + 1);
      return;
    }
    const conductorDrag = conductorDragRef.current;
    if (conductorDrag) {
      if (svgRef.current?.hasPointerCapture?.(conductorDrag.pointerId)) {
        svgRef.current.releasePointerCapture(conductorDrag.pointerId);
      }
      conductorDragRef.current = null;
      if (!conductorDrag.moved) return;
      historyRef.current.past.push(conductorDrag.startDocument);
      historyRef.current.future = [];
      setHistoryVersion(version => version + 1);
      return;
    }
    if (marqueeRef.current) {
      marqueeRef.current = null;
      setMarquee(null);
      return;
    }
    const drag = dragRef.current;
    if (drag && svgRef.current?.hasPointerCapture?.(drag.pointerId)) {
      svgRef.current.releasePointerCapture(drag.pointerId);
    }
    dragRef.current = null;
    if (!drag?.moved) return;
    let groupedDocument = documentRef.current;
    drag.turretBoardTargets.forEach((boardId, turretId) => {
      const board = groupedDocument.objects.find(object => object.id === boardId);
      if (!board) return;
      idCounter.current += 1;
      groupedDocument = createLayoutGroup(
        groupedDocument,
        `group-${Date.now()}-${idCounter.current}`,
        [...getLayoutGroupObjectIds(groupedDocument, board.id), turretId]
      );
    });
    if (groupedDocument !== documentRef.current) applyDocumentChange(groupedDocument, false);
    historyRef.current.past.push(drag.startDocument);
    historyRef.current.future = [];
    setHistoryVersion(version => version + 1);
  };

  pointerHandlersRef.current = { move: dragObject, finish: finishDrag };

  useEffect(() => {
    const moveActivePointer = (event) => {
      if (dragRef.current || conductorDragRef.current || leadDragRef.current || marqueeRef.current) {
        pointerHandlersRef.current.move(event);
      }
    };
    const finishActivePointer = () => {
      if (dragRef.current || conductorDragRef.current || leadDragRef.current || marqueeRef.current) {
        pointerHandlersRef.current.finish();
      }
    };
    window.addEventListener('pointermove', moveActivePointer);
    window.addEventListener('pointerup', finishActivePointer);
    window.addEventListener('pointercancel', finishActivePointer);
    return () => {
      window.removeEventListener('pointermove', moveActivePointer);
      window.removeEventListener('pointerup', finishActivePointer);
      window.removeEventListener('pointercancel', finishActivePointer);
    };
  }, []);

  return (
    <section className="layout-studio" aria-label="Layout Studio">
      <ComponentPalette canvas={document.canvas} units={displayUnits} onAdd={addObject} />

      <main className="layout-workspace">
        <div className="layout-workspace-bar">
          <div className="layout-mode-control" aria-label="Layout tools">
            <button type="button" className={editorMode === 'select' ? 'is-active' : ''} onClick={() => chooseMode('select')}>Select</button>
            <button type="button" className={editorMode === 'wire' ? 'is-active' : ''} onClick={() => chooseMode('wire')}>Wire</button>
          </div>
          <div className="layout-history-control" role="group" aria-label="Edit history">
            <button type="button" onClick={undo} disabled={!canUndo} aria-label="Undo">Undo</button>
            <button type="button" onClick={redo} disabled={!canRedo} aria-label="Redo">Redo</button>
          </div>
          <div className="layout-zoom-control" role="group" aria-label="Canvas zoom">
            <button type="button" aria-label="Zoom out" title="Zoom out" disabled={zoomLevel <= MIN_ZOOM} onClick={() => setZoom(zoomLevel - ZOOM_STEP)}>-</button>
            <button type="button" className="layout-zoom-value" aria-label="Reset zoom" title="Reset zoom" onClick={() => setZoom(1)}>{Math.round(zoomLevel * 100)}%</button>
            <button type="button" aria-label="Zoom in" title="Zoom in" disabled={zoomLevel >= MAX_ZOOM} onClick={() => setZoom(zoomLevel + ZOOM_STEP)}>+</button>
          </div>
          <div className="layout-units-control" role="group" aria-label="Drawing units">
            <button type="button" className={displayUnits === 'cm' ? 'is-active' : ''} aria-pressed={displayUnits === 'cm'} onClick={() => setUnits('cm')}>cm</button>
            <button type="button" className={displayUnits === 'in' ? 'is-active' : ''} aria-pressed={displayUnits === 'in'} onClick={() => setUnits('in')}>in</button>
          </div>
          <div className="layout-resistor-display" role="group" aria-label="Resistor display">
            <span>Resistors</span>
            <button type="button" className={resistorDisplay === 'bands' ? 'is-active' : ''} aria-pressed={resistorDisplay === 'bands'} onClick={() => setResistorDisplay('bands')}>Bands</button>
            <button type="button" className={resistorDisplay === 'text' ? 'is-active' : ''} aria-pressed={resistorDisplay === 'text'} onClick={() => setResistorDisplay('text')}>Text</button>
          </div>
          {resistorDisplay === 'bands' && (
            <button type="button" className="layout-reference-button" onClick={() => setShowResistorSheet(true)}>Resistor key</button>
          )}
          <label className="layout-calibration-toggle">
            <input type="checkbox" checked={printCalibration} onChange={event => setPrintCalibration(event.target.checked)} />
            Print check
          </label>
          <label className="layout-rulers-toggle">
            <input type="checkbox" checked={showRulers} onChange={event => setShowRulers(event.target.checked)} />
            Rulers
          </label>
          <GridField value={document.canvas.grid} units={displayUnits} onChange={setGridSpacing} />
          <span className="layout-mode-status">
            {pendingEndpoint ? 'Choose the destination terminal' : editorMode === 'wire' ? 'Choose a starting terminal' : 'Top assembly view'}
          </span>
        </div>
        <div ref={canvasWrapRef} className="layout-canvas-wrap" data-testid="layout-canvas-scroll">
          <svg
            ref={svgRef}
            className="layout-canvas"
            style={canvasScale ? {
              width: `${document.canvas.width * canvasScale}px`,
              height: `${document.canvas.height * canvasScale}px`,
            } : undefined}
            viewBox={`0 0 ${document.canvas.width} ${document.canvas.height}`}
            role="img"
            aria-label="Physical amplifier layout canvas"
            onPointerDown={beginMarquee}
            onContextMenu={openCanvasObjectContextMenu}
          >
            <defs>
              <linearGradient id="layout-resistor-ceramic" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#9f665a" />
                <stop offset="0.14" stopColor="#e6aaa0" />
                <stop offset="0.34" stopColor="#d99a91" />
                <stop offset="0.66" stopColor="#ca877e" />
                <stop offset="0.86" stopColor="#b8756c" />
                <stop offset="1" stopColor="#91584f" />
              </linearGradient>
              <linearGradient id="layout-resistor-ceramic-shade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#542d29" stopOpacity="0.32" />
                <stop offset="0.18" stopColor="#fff4ed" stopOpacity="0.24" />
                <stop offset="0.45" stopColor="#ffffff" stopOpacity="0.04" />
                <stop offset="1" stopColor="#572f2a" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="layout-resistor-carbon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#5a3425" />
                <stop offset="0.16" stopColor="#87553c" />
                <stop offset="0.38" stopColor="#75462f" />
                <stop offset="0.68" stopColor="#663a29" />
                <stop offset="1" stopColor="#4c2a20" />
              </linearGradient>
              <linearGradient id="layout-resistor-iskra" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#b47f12" />
                <stop offset="0.14" stopColor="#f6d66a" />
                <stop offset="0.34" stopColor="#eec548" />
                <stop offset="0.66" stopColor="#dda92d" />
                <stop offset="0.86" stopColor="#c58b18" />
                <stop offset="1" stopColor="#94620d" />
              </linearGradient>
              <linearGradient id="layout-resistor-piher" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#552128" />
                <stop offset="0.14" stopColor="#a94d55" />
                <stop offset="0.34" stopColor="#8f3540" />
                <stop offset="0.66" stopColor="#782a34" />
                <stop offset="0.86" stopColor="#64232d" />
                <stop offset="1" stopColor="#431820" />
              </linearGradient>
              <linearGradient id="layout-resistor-cylinder-shade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#130b08" stopOpacity="0.48" />
                <stop offset="0.18" stopColor="#fff4df" stopOpacity="0.22" />
                <stop offset="0.42" stopColor="#ffffff" stopOpacity="0.05" />
                <stop offset="0.72" stopColor="#1c0f0a" stopOpacity="0.08" />
                <stop offset="1" stopColor="#120a08" stopOpacity="0.45" />
              </linearGradient>
              <linearGradient id="layout-resistor-metal-film" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#147ca3" />
                <stop offset="0.14" stopColor="#79dbf4" />
                <stop offset="0.34" stopColor="#42c3e8" />
                <stop offset="0.66" stopColor="#28aacf" />
                <stop offset="0.86" stopColor="#168caf" />
                <stop offset="1" stopColor="#0e6888" />
              </linearGradient>
              <linearGradient id="layout-resistor-metal-film-shade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#06394b" stopOpacity="0.34" />
                <stop offset="0.18" stopColor="#e8fbff" stopOpacity="0.34" />
                <stop offset="0.46" stopColor="#ffffff" stopOpacity="0.05" />
                <stop offset="1" stopColor="#073c4e" stopOpacity="0.34" />
              </linearGradient>
              <linearGradient id="layout-resistor-metal-oxide" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#838989" />
                <stop offset="0.14" stopColor="#d8dddd" />
                <stop offset="0.34" stopColor="#bec4c4" />
                <stop offset="0.68" stopColor="#aeb4b4" />
                <stop offset="0.88" stopColor="#979d9d" />
                <stop offset="1" stopColor="#747a7a" />
              </linearGradient>
              <linearGradient id="layout-resistor-metal-oxide-shade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#444949" stopOpacity="0.25" />
                <stop offset="0.18" stopColor="#ffffff" stopOpacity="0.3" />
                <stop offset="0.46" stopColor="#ffffff" stopOpacity="0.04" />
                <stop offset="0.8" stopColor="#555a5a" stopOpacity="0.08" />
                <stop offset="1" stopColor="#373c3c" stopOpacity="0.28" />
              </linearGradient>
              <radialGradient id="layout-dark-phenolic" cx="38%" cy="30%" r="72%">
                <stop offset="0" stopColor="#6b5550" />
                <stop offset="0.48" stopColor="#392e2c" />
                <stop offset="1" stopColor="#17191a" />
              </radialGradient>
              <radialGradient id="layout-mcmurdo-phenolic" cx="36%" cy="28%" r="74%">
                <stop offset="0" stopColor="#9a5e3e" />
                <stop offset="0.36" stopColor="#774127" />
                <stop offset="0.72" stopColor="#552d1d" />
                <stop offset="1" stopColor="#311b15" />
              </radialGradient>
              <linearGradient id="layout-mcmurdo-metal" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#7f896d" />
                <stop offset="0.2" stopColor="#e3dfc4" />
                <stop offset="0.48" stopColor="#9ca080" />
                <stop offset="0.72" stopColor="#d5cfad" />
                <stop offset="1" stopColor="#68725d" />
              </linearGradient>
              <radialGradient id="layout-pec-can" cx="35%" cy="28%" r="76%">
                <stop offset="0" stopColor="#d9dcda" />
                <stop offset="0.28" stopColor="#949a99" />
                <stop offset="0.62" stopColor="#686e6e" />
                <stop offset="0.86" stopColor="#858b8a" />
                <stop offset="1" stopColor="#3e4444" />
              </radialGradient>
              <radialGradient id="layout-brushed-metal" cx="34%" cy="28%" r="75%">
                <stop offset="0" stopColor="#f3f5f3" />
                <stop offset="0.5" stopColor="#c4ccca" />
                <stop offset="1" stopColor="#7e8a8a" />
              </radialGradient>
              <linearGradient id="layout-transformer-bell-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#596364" />
                <stop offset="0.48" stopColor="#293132" />
                <stop offset="1" stopColor="#111617" />
              </linearGradient>
              <pattern id="layout-minor-grid" width={document.canvas.grid} height={document.canvas.grid} patternUnits="userSpaceOnUse">
                <path d={`M ${document.canvas.grid} 0 L 0 0 0 ${document.canvas.grid}`} className="layout-grid-minor" />
              </pattern>
              <pattern id="layout-major-grid" width={majorGridSpacing} height={majorGridSpacing} patternUnits="userSpaceOnUse">
                <rect width={majorGridSpacing} height={majorGridSpacing} fill="url(#layout-minor-grid)" />
                <path d={`M ${majorGridSpacing} 0 L 0 0 0 ${majorGridSpacing}`} className="layout-grid-major" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#layout-major-grid)" data-layout-export-background="true" data-layout-background="true" data-testid="layout-canvas-background" />
            {printCalibration && <PrintCalibrationRule canvas={document.canvas} />}
            {showRulers && <CanvasRulers canvas={document.canvas} units={displayUnits} />}
            {marquee && (
              <rect
                x={marquee.x}
                y={marquee.y}
                width={marquee.width}
                height={marquee.height}
                className="layout-selection-marquee"
                data-layout-export-exclude="true"
              />
            )}
            {getLayoutStack(document).map(({ kind, item }) => (
              kind === 'conductor' ? (
                <LayoutConductor
                  key={`conductor:${item.id}`}
                  conductor={item}
                  document={document}
                  partner={item.braid?.id
                    ? document.conductors.find(conductor => conductor.id !== item.id && conductor.braid?.id === item.braid.id)
                    : null}
                  selected={selectedConductorIds.has(item.id)}
                  onPointerDown={(event) => beginConductorDrag(event, item.id)}
                />
              ) : (
                <LayoutObject
                  key={`object:${item.id}`}
                  object={resolveLeadAttachments(document, item)}
                  resistorDisplay={resistorDisplay}
                  selected={selectedIds.has(item.id)}
                  wireMode={editorMode === 'wire'}
                  showTerminalTargets={leadSnapActive && !selectedIds.has(item.id)}
                  pendingEndpoint={pendingEndpoint}
                  onPointerDown={beginDrag}
                  onTerminalPointerDown={chooseTerminal}
                  onLeadHandlePointerDown={beginLeadDrag}
                />
              )
            ))}
          </svg>
          {document.objects.length === 0 && (
            <div className="layout-empty-state">
              <strong>Build a physical amplifier layout</strong>
              <span>Add a physical part to begin.</span>
            </div>
          )}
        </div>
      </main>

      <aside className="layout-properties">
        <div className="layout-panel-heading">
          <span>Properties</span>
          {selectedObject && (
            <button
              type="button"
              className="layout-object-menu-trigger"
              aria-label="Open object actions"
              title="Object actions"
              onClick={openSelectedObjectMenu}
            >
              &#8942;
            </button>
          )}
        </div>
        {!selectedObject && selectedConductors.length === 0 ? (
          <p className="layout-properties-empty">Select an object to inspect its physical dimensions.</p>
        ) : selectedConductors.length > 1 ? (
          <div className="layout-properties-form">
            <p className="layout-selection-count">{selectedConductors.length} wires selected</p>
            <button type="button" onClick={toggleSelectedBraid} disabled={selectedConductors.length !== 2}>
              {selectedConductors.length === 2
                && selectedConductors[0].braid?.id
                && selectedConductors[0].braid.id === selectedConductors[1].braid?.id
                ? 'Unbraid wires' : 'Braid wires'}
            </button>
            <button type="button" className="layout-delete-button" onClick={deleteSelected}>Delete wires</button>
          </div>
        ) : selectedConductor ? (
          <div className="layout-properties-form">
            <div className="layout-field">
              <span>Wire color</span>
              <div className="layout-color-swatches">
                {BRIGHT_WIRE_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Wire color ${color}`}
                    className={selectedConductor.color === color ? 'is-active' : ''}
                    style={{ background: color }}
                    onClick={() => updateSelectedConductor({ color })}
                  />
                ))}
              </div>
            </div>
            <label className="layout-field">
              <span>Wire gauge</span>
              <select value={selectedConductor.gauge} onChange={(event) => updateSelectedConductor({ gauge: Number(event.target.value) })}>
                {[18, 20, 22, 24].map(gauge => <option key={gauge} value={gauge}>{gauge} AWG</option>)}
              </select>
            </label>
            <StackControls onReorder={reorderSelected} />
            <button type="button" className="layout-delete-button" onClick={deleteSelected}>Delete wire</button>
          </div>
        ) : (
          <div className="layout-properties-form">
            {selectedIds.size > 1 && <p className="layout-selection-count">{selectedIds.size} objects selected</p>}
            {selectedIds.size > 1 && (
              <div className="layout-arrange-controls" aria-label="Arrange selected objects">
                <span>Align</span>
                <div>
                  <button type="button" onClick={() => alignSelected('x', 'start')}>Left</button>
                  <button type="button" onClick={() => alignSelected('x', 'center')}>Center</button>
                  <button type="button" onClick={() => alignSelected('x', 'end')}>Right</button>
                  <button type="button" onClick={() => alignSelected('y', 'start')}>Top</button>
                  <button type="button" onClick={() => alignSelected('y', 'center')}>Middle</button>
                  <button type="button" onClick={() => alignSelected('y', 'end')}>Bottom</button>
                </div>
                <span>Distribute</span>
                <div>
                  <button type="button" disabled={selectedIds.size < 3} onClick={() => distributeSelected('x')}>Horizontal</button>
                  <button type="button" disabled={selectedIds.size < 3} onClick={() => distributeSelected('y')}>Vertical</button>
                </div>
              </div>
            )}
            {selectedIds.size > 1 && (
              <div className="layout-group-controls" role="group" aria-label="Object grouping">
                <button type="button" onClick={groupSelected}>Group</button>
                <button
                  type="button"
                  disabled={!document.groups.some(group => group.objectIds.some(id => selectedIds.has(id)))}
                  onClick={ungroupSelected}
                >
                  Ungroup
                </button>
              </div>
            )}
            <label className="layout-field">
              <span>{['resistor', 'capacitor', 'diode', 'potentiometer', 'input-jack', 'parallel-output-jacks', 'power-transformer', 'output-transformer', 'choke'].includes(selectedObject.type) ? 'Reference' : 'Name'}</span>
              <input value={selectedObject.label} onChange={(event) => updateSelected({ label: event.target.value })} />
            </label>
            <div className="layout-field">
              <span>Rotation</span>
              <div className="layout-rotation-controls" role="group" aria-label="Rotation presets">
                {[0, 90, 180, 270].map(rotation => (
                  <button
                    key={rotation}
                    type="button"
                    className={normalizeRotation(selectedObject.rotation || 0) === rotation ? 'is-active' : ''}
                    onClick={() => rotateContextSelection(rotation)}
                  >
                    {rotation}°
                  </button>
                ))}
              </div>
              <label className="layout-rotation-angle">
                <input
                  type="number"
                  step="1"
                  value={normalizeRotation(selectedObject.rotation || 0)}
                  aria-label="Rotation degrees"
                  onChange={(event) => {
                    const rotation = Number(event.target.value);
                    if (Number.isFinite(rotation)) rotateContextSelection(normalizeRotation(rotation));
                  }}
                />
                <span>°</span>
              </label>
            </div>
            {['resistor', 'capacitor', 'diode', 'potentiometer', 'impedance-selector', 'power-transformer', 'output-transformer', 'choke'].includes(selectedObject.type) && selectedObject.packageStyle !== 'can' && (
              <label className="layout-field">
                <span>Value</span>
                <input value={selectedObject.value} onChange={(event) => updateSelected({ value: event.target.value })} />
              </label>
            )}
            {selectedObject.type === 'potentiometer' && (
              <label className="layout-field">
                <span>Taper</span>
                <select value={selectedObject.taper} onChange={(event) => updateSelected({ taper: event.target.value })}>
                  <option value="Audio">Audio (log)</option>
                  <option value="Linear">Linear</option>
                  <option value="Reverse audio">Reverse audio</option>
                </select>
              </label>
            )}
            {selectedObject.type === 'resistor' && (
              <>
                <label className="layout-field">
                  <span>Construction</span>
                  <select value={selectedObject.construction || 'carbon-composition'} onChange={(event) => updatePassiveConstruction(event.target.value)}>
                    {Object.entries(RESISTOR_LIBRARY).map(([value, definition]) => <option key={value} value={value}>{definition.name}</option>)}
                  </select>
                </label>
                <label className="layout-field">
                  <span>Wattage</span>
                  <select value={getResistorWattage(selectedObject)} onChange={(event) => updateSelected({ wattage: Number(event.target.value) })}>
                    {getResistorWattageOptions(selectedObject.construction || 'carbon-composition').map(wattage => (
                      <option key={wattage} value={wattage}>{wattage} W</option>
                    ))}
                  </select>
                </label>
              </>
            )}
            {selectedObject.type === 'diode' && (
              <label className="layout-field">
                <span>Diode type</span>
                <select value={selectedObject.construction || 'rectifier'} onChange={(event) => updateDiodeConstruction(event.target.value)}>
                  {Object.entries(DIODE_LIBRARY).map(([value, definition]) => <option key={value} value={value}>{definition.name}</option>)}
                </select>
              </label>
            )}
            {selectedObject.type === 'capacitor' && (
              <>
                <label className="layout-field">
                  <span>Construction</span>
                  <select value={selectedObject.construction || 'axial-film'} onChange={(event) => updatePassiveConstruction(event.target.value)}>
                    {Object.entries(CAPACITOR_LIBRARY).map(([value, definition]) => <option key={value} value={value}>{definition.name}</option>)}
                  </select>
                </label>
                {selectedObject.packageStyle === 'can' && (
                  <CanCapacitorSectionEditor
                    sections={selectedObject.sections || DEFAULT_CAN_CAPACITOR_SECTIONS}
                    onChange={updateCanCapacitorSection}
                    onAdd={addCanCapacitorSection}
                    onRemove={removeCanCapacitorSection}
                  />
                )}
                {['silver-mica', 'board-axial-electrolytic', 'sprague-30d-electrolytic', 'axial-electrolytic', 'radial-electrolytic', 'can-electrolytic'].includes(selectedObject.construction) && (
                  <label className="layout-field">
                    <span>Voltage rating</span>
                    <div className="layout-number-input">
                      <input
                        type="number"
                        min={selectedObject.construction === 'can-electrolytic' ? 350 : selectedObject.construction === 'silver-mica' ? 100 : selectedObject.construction === 'axial-electrolytic' ? 50 : 10}
                        max={selectedObject.construction === 'silver-mica' ? 1000 : selectedObject.construction === 'can-electrolytic' ? 500 : ['board-axial-electrolytic', 'sprague-30d-electrolytic'].includes(selectedObject.construction) ? 100 : 450}
                        aria-label="Voltage rating"
                        value={selectedObject.voltageRating || ''}
                        onChange={(event) => {
                          const voltageRating = Number(event.target.value);
                          updateSelected(['board-axial-electrolytic', 'sprague-30d-electrolytic'].includes(selectedObject.construction)
                            ? { voltageRating, ...getBoardAxialElectrolyticSize(voltageRating) }
                            : { voltageRating });
                        }}
                      />
                      <span>V</span>
                    </div>
                  </label>
                )}
                {['board-axial-electrolytic', 'axial-electrolytic', 'radial-electrolytic'].includes(selectedObject.construction) && (
                  <div className="layout-field">
                    <span>Sleeve color</span>
                    <div className="layout-color-swatches">
                      {ELECTROLYTIC_SLEEVE_COLORS.map(color => (
                        <button
                          key={color.value}
                          type="button"
                          aria-label={`${color.name} sleeve`}
                          className={selectedObject.sleeveColor === color.value ? 'is-active' : ''}
                          style={{ background: color.value }}
                          onClick={() => updateSelected({ sleeveColor: color.value })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {['power-transformer', 'output-transformer', 'choke'].includes(selectedObject.type) && (
              <label className="layout-field">
                <span>Mount style</span>
                <select value={selectedObject.mountStyle || 'standup'} onChange={(event) => updateSelected({ mountStyle: event.target.value })}>
                  <option value="laydown">Laydown (windings exposed)</option>
                  <option value="standup">Standup (bell housing)</option>
                </select>
              </label>
            )}
            {selectedObject.type === 'power-transformer' && (
              <PowerTransformerTapEditor
                taps={getPowerTransformerTaps(selectedObject)}
                onChange={updateTransformerTap}
                onAdd={addTransformerTap}
                onRemove={removeTransformerTap}
              />
            )}
            <NumberField label="X" value={selectedObject.x} units={displayUnits} onChange={x => updateSelected({ x })} />
            <NumberField label="Y" value={selectedObject.y} units={displayUnits} onChange={y => updateSelected({ y })} />
            <NumberField label="Width" value={selectedObject.width} units={displayUnits} min={5} onChange={width => updateSelected({ width })} />
            <NumberField label="Height" value={selectedObject.height} units={displayUnits} min={5} onChange={height => updateSelected({ height })} />
            {selectedObject.type === 'turret-board' && (
              <>
                <div className="layout-field-row">
                  <label className="layout-field">
                    <span>Rows</span>
                    <input type="number" min="1" max="8" value={selectedObject.rows} onChange={(event) => updateSelected({ rows: Number(event.target.value) })} />
                  </label>
                  <label className="layout-field">
                    <span>Columns</span>
                    <input type="number" min="2" max="40" value={selectedObject.columns} onChange={(event) => updateSelected({ columns: Number(event.target.value) })} />
                  </label>
                </div>
                <NumberField
                  label="Horizontal turret spacing"
                  value={getTurretGridGeometry(selectedObject).spacingX}
                  units={displayUnits}
                  min={0.5}
                  onChange={turretSpacingX => updateSelected({ turretSpacingX })}
                />
                <NumberField
                  label="Vertical turret spacing"
                  value={getTurretGridGeometry(selectedObject).spacingY}
                  units={displayUnits}
                  min={0.5}
                  onChange={turretSpacingY => updateSelected({ turretSpacingY })}
                />
              </>
            )}
          </div>
        )}
      </aside>
      {contextMenu && document.objects.some(object => object.id === contextMenu.objectId) && createPortal(
        <ObjectContextMenu
          menu={contextMenu}
          object={document.objects.find(object => object.id === contextMenu.objectId)}
          selectedCount={selectedIds.size}
          onRotate={rotateContextSelection}
          onReorder={(direction) => {
            reorderSelected(direction);
            setContextMenu(null);
          }}
          onDuplicate={() => {
            duplicateSelected();
            setContextMenu(null);
          }}
          onDelete={() => {
            deleteSelected();
            setContextMenu(null);
          }}
          onClose={() => setContextMenu(null)}
        />,
        window.document.body
      )}
      {showResistorSheet && <ResistorReferenceSheet resistors={resistors} onClose={() => setShowResistorSheet(false)} />}
    </section>
  );
});

LayoutStudio.displayName = 'LayoutStudio';

export default LayoutStudio;